// Typed wrappers around /admin/* on the Hono Lambda. Each helper returns a
// strongly-typed response and is consumed via TanStack Query in the routes.
//
// Shapes mirror the Hono handlers in apps/api/src/modules/admin/*.

import { api, apiRaw } from './api';
import type { PartnerAuthProvider } from '@drivecars/shared';

export interface AdminDashboardResponse {
  kpis: {
    gmv30d: number;
    bookings7d: number;
    pendingApprovals: number;
    flaggedItems: number;
    activeUsers: number;
    publishedCars: number;
    vendors: number;
    bookingStatusCounts: Record<string, number>;
  };
  queues: {
    pendingBookings: number;
    pendingCars: number;
    pendingReviews: number;
    flaggedReviews: number;
    pendingPayouts: number;
  };
  gmvBuckets: Array<{ day: string; total: number; count: number }>;
  recentBookings: Array<{
    id: number;
    reference: string;
    status: string;
    total: number;
    currency: string;
    dateFrom: string;
    dateTo: string;
    createdAt: string;
    carSlug: string | null;
    customerName: string;
    customerEmail: string | null;
  }>;
  auditLog: Array<{
    id: string;
    action: string;
    entityType: string;
    entityId: string;
    before: unknown;
    after: unknown;
    createdAt: string;
    actorEmail: string | null;
  }>;
}

export function fetchAdminDashboard(signal?: AbortSignal): Promise<AdminDashboardResponse> {
  return api<AdminDashboardResponse>({ path: '/admin/dashboard', signal });
}

// ─── Overview ────────────────────────────────────────────────────────
//
// `revenue` / `drivecarsRevenue` / `partnerCommissions` are LISTS, one entry
// per currency, and there is deliberately no total field: a booking's money
// is in the currency the customer was charged, and there is no rate that
// could add MAD to EUR and still be true next year. The UI prints one CARD
// per measure, listing its currencies as rows. See
// apps/api/src/modules/admin/overview.ts.
//
// The booking-derived figures are bounded by a month PERIOD; the stock counts
// (partners, crews, published cities/countries) are not. The response echoes
// the window it used, so the screen can caption the figures even when it sent
// no period at all.

export interface AdminOverviewMoney {
  currency: string;
  amount: number;
}

export interface AdminOverviewResponse {
  /** Confirmed + completed, test bookings excluded. */
  bookings: number;
  cancelled: number;
  /** How many rows the test-customer/test-crew predicate dropped. */
  testBookingsExcluded: number;
  revenue: AdminOverviewMoney[];
  drivecarsRevenue: AdminOverviewMoney[];
  /** What partners earned, per currency. A separate measure, not a slice of
   *  `drivecarsRevenue` — commission is already subtracted from that. */
  partnerCommissions: AdminOverviewMoney[];
  /** What CREWS owe DriveCars, per currency, on Model 2/3 bookings whose base
   *  they collected in cash (0241). A fourth measure, not a slice of anything
   *  above: it is already folded into `drivecarsRevenue` and appears in no
   *  customer's price. Empty while every sale is the booking fee. */
  supplierCommissions: AdminOverviewMoney[];
  partners: number;
  crews: number;
  publishedCities: number;
  publishedCountries: number;
  byCountry: Array<{ code: string; name: string | null; bookings: number }>;
  byCity: Array<{
    id: number;
    slug: string;
    name: string | null;
    countryCode: string | null;
    bookings: number;
  }>;
  /** The window the booking figures cover, as the server resolved it. */
  period: { from: string; to: string };
}

/** `period` is both bounds or neither — the API refuses one on its own with a
 *  400, so an omitted period means "you choose", which it answers with the
 *  calendar year to date. */
export function fetchOverview(
  period?: { from: string; to: string },
  signal?: AbortSignal,
): Promise<AdminOverviewResponse> {
  const query = period ? `?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}` : '';
  return api<AdminOverviewResponse>({ path: `/admin/overview${query}`, signal });
}

// ─── Bookings ────────────────────────────────────────────────────────

export interface AdminBookingsListParams {
  status?: 'draft' | 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'all' | 'active' | 'archived';
  paymentMethod?: 'card' | 'noncard' | 'all';
  /** Rentals and transfers are worked as separate queues — see Bookings.tsx. */
  product?: 'car-rentals' | 'airport-transfers' | 'ride-hailing' | 'all';
  q?: string;
  page?: number;
  limit?: number;
  sort?: 'created_at' | 'total' | 'date_from';
  order?: 'asc' | 'desc';
}

export interface AdminBookingRow {
  id: number;
  reference: string;
  status: string;
  paymentStatus: string | null;
  paymentProvider: string | null;
  units: number;
  total: number;
  currency: string;
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  updatedAt: string;
  carId: number;
  customerId: number;
  vendorId: number;
  carSlug: string | null;
  carTitle: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
  vendorEmail: string | null;
  vendorBusinessName: string | null;
  customerName: string;
  vendorName: string | null;
  /** What DriveCars keeps on this booking, in `moneyCurrency`. An estimate
   *  until the bid settles (see AdminBookingDetail.money.settled). */
  drivecarsNet?: number;
  /** The partner's cut, in `moneyCurrency`. */
  partnerCommission?: number;
  /** Currency of `drivecarsNet` + `partnerCommission` — the currency the
   *  customer was charged, which is NOT always the ledger `currency`. */
  moneyCurrency?: string;
}

export interface AdminBookingsListResponse {
  rows: AdminBookingRow[];
  total: number;
  page: number;
  limit: number;
}

function buildQuery(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function fetchAdminBookings(
  params: AdminBookingsListParams = {},
  signal?: AbortSignal,
): Promise<AdminBookingsListResponse> {
  return api<AdminBookingsListResponse>({
    path: `/admin/bookings${buildQuery(params)}`,
    signal,
  });
}

// ─── Cars ────────────────────────────────────────────────────────────

export type AdminCarsTestCrewFilter = 'exclude' | 'only' | 'all';

export interface AdminCarsListParams {
  status?: 'draft' | 'pending' | 'published' | 'archived' | 'all';
  q?: string;
  /** ISO 3166-1 alpha-2; matches the car's own country_code anchor. */
  countryCode?: string;
  locationId?: number;
  page?: number;
  limit?: number;
  sort?: 'created_at' | 'price' | 'status';
  order?: 'asc' | 'desc';
  /** Test-mode crew (vendor id 99000) visibility. Default 'exclude' on
   *  the API side hides ~200 cloned test cars from the moderation queue;
   *  pass 'only' for QA management, 'all' to include both. */
  testCrew?: AdminCarsTestCrewFilter;
}

export interface AdminCarRow {
  id: number;
  slug: string;
  status: string;
  price: number;
  pricingCurrency: string;
  priceUnit: string | null;
  isFeatured: boolean | null;
  reviewCount: number | null;
  reviewScore: number | null;
  createdAt: string;
  updatedAt: string;
  vendorId: number;
  locationId: number;
  /** Canonical display title: Car catalog title with raw-listing fallback. */
  title: string | null;
  /** Raw vendor-entered listing title (cars_locales en). */
  listingTitle?: string | null;
  locationSlug: string | null;
  locationTitle: string | null;
  vendorEmail: string | null;
  vendorBusinessName: string | null;
  imageUrl: string | null;
  imageThumb: string | null;
  vendorName: string | null;
}

export interface AdminCarsListResponse {
  rows: AdminCarRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminCars(
  params: AdminCarsListParams = {},
  signal?: AbortSignal,
): Promise<AdminCarsListResponse> {
  return api<AdminCarsListResponse>({
    path: `/admin/cars${buildQuery(params)}`,
    signal,
  });
}

// ─── Vendors ─────────────────────────────────────────────────────────

export interface AdminVendorsListParams {
  status?: 'active' | 'pending' | 'suspended' | 'all';
  verification?: 'approved' | 'pending' | 'rejected' | 'unverified' | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminVendorRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: string;
  verificationStatus: string | null;
  createdAt: string;
  businessName: string | null;
  businessAddress: string | null;
  commissionType: string | null;
  commissionValue: number | null;
  taxId: string | null;
  planId: number | null;
  planName: string | null;
  avatarUrl: string | null;
  carsTotal: number;
  bookingsTotal: number;
  displayName: string;
  isHouse: boolean;
  source: string | null;
  autoAssign: boolean;
  /** Fixed-mode partners only show crews that opted in to honour their
   *  advertised prices. Set by the crew app, cleared by ops (see
   *  VendorPatchInput.fixedPriceOptin below). */
  fixedPriceOptin: boolean;
  fixedPriceOptinAt: string | null;
  /** Daily re-attestation stamp; compared against the UTC calendar day to
   *  derive the crew-list "confirmed today" / "not confirmed today" badge. */
  pricesConfirmedAt: string | null;
}

export interface AdminVendorsListResponse {
  rows: AdminVendorRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminVendors(
  params: AdminVendorsListParams = {},
  signal?: AbortSignal,
): Promise<AdminVendorsListResponse> {
  return api<AdminVendorsListResponse>({
    path: `/admin/vendors${buildQuery(params)}`,
    signal,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────

export interface MutationResponse {
  ok: true;
  id: number;
  status?: string;
  reference?: string;
  isFeatured?: boolean | null;
  verificationStatus?: string | null;
}

function mutate<T>(path: string, body?: object): Promise<T> {
  return api<T>({ path, method: 'POST', body: body ?? {} });
}

// Bookings
export const confirmBooking = (ref: string) =>
  mutate<MutationResponse>(`/admin/bookings/${encodeURIComponent(ref)}/confirm`);
export const cancelBooking = (ref: string, reason?: string) =>
  mutate<MutationResponse>(`/admin/bookings/${encodeURIComponent(ref)}/cancel`, { reason });
export const completeBooking = (ref: string) =>
  mutate<MutationResponse>(`/admin/bookings/${encodeURIComponent(ref)}/complete`);

// Mark host payout paid — klarna/crypto only. The backend 409s for card
// (auto-settled) and for already-paid bookings; callers should surface the
// error so finance doesn't blindly retry.
export interface MarkHostPayoutPaidResponse {
  ok: true;
  reference: string;
  completedAt: string;
}
export const markHostPayoutPaid = (ref: string) =>
  mutate<MarkHostPayoutPaidResponse>(
    `/admin/bookings/${encodeURIComponent(ref)}/mark-host-payout-paid`,
  );

// Host-payout queue — klarna/crypto bookings whose pickup has passed and
// whose `host_payout_completed_at` is still null. The Mark-paid action on
// each row reuses `markHostPayoutPaid` above (Task 21).
export interface HostPayoutQueueRow {
  reference: string;
  paymentMethod: 'klarna' | 'crypto';
  paymentLockedAmountMinor: number | null;
  paymentLockedCurrency: string | null;
  dateTo: string;
  bidAmountBuy: number | null;
  buyCurrency: string | null;
  customerName: string;
  vendorName: string;
}

export interface HostPayoutQueueResponse {
  rows: HostPayoutQueueRow[];
  total: number;
  page: number;
  limit: number;
}

export interface PageParams {
  page?: number;
  limit?: number;
}

export function fetchHostPayoutQueue(
  params: PageParams = {},
  signal?: AbortSignal,
): Promise<HostPayoutQueueResponse> {
  return api<HostPayoutQueueResponse>({
    path: `/admin/host-payout-queue${buildQuery(params)}`,
    signal,
  });
}

// Match — admin sets a negotiated base price + assigns a host for a
// `pending` booking. Backend POST /admin/bookings/:reference/match returns
// one of the union variants below; on a domain error (404/409) the Hono
// route returns `{ error }` with a non-2xx status, so `api()` throws an
// ApiError whose `.body.error` carries the reason — callers should catch.
export type MatchBookingResult =
  | { kind: 'confirmed' }
  | { kind: 'repayment_required'; payLink: string };

export function matchBooking(
  reference: string,
  input: { basePrice: number; hostId: number; currency?: string },
): Promise<MatchBookingResult> {
  return mutate<MatchBookingResult>(
    `/admin/bookings/${encodeURIComponent(reference)}/match`,
    input,
  );
}

// Payment request — admin mints a booking-tied payment link to collect an
// extra charge (price-fix top-up, deposit, damage) on an existing booking.
// Backend POST /admin/bookings/:reference/payment-request returns the link.
export type PaymentRequestReason = 'top_up' | 'deposit' | 'damage_charge';
export interface PaymentRequestResult {
  token: string;
  url: string;
  expiresAt: string;
}

export function requestBookingPayment(
  reference: string,
  input: { amount: number; currency: string; reason?: PaymentRequestReason },
): Promise<PaymentRequestResult> {
  return mutate<PaymentRequestResult>(
    `/admin/bookings/${encodeURIComponent(reference)}/payment-request`,
    input,
  );
}

// Crew bids on a booking (host_id = the crew member's user id). Cheapest first.
// "Use this bid" calls matchBooking({ basePrice: amount, hostId }).
export interface AdminBookingBid {
  id: number;
  hostId: number;
  /** EUR-normalised per-day figure — for display and ordering only. */
  amount: number;
  /** What the crew typed, and the currency they typed it in. THIS is what
   *  "Use this bid" sends to /match; null on legacy rows. */
  amountOriginal?: number | null;
  currency?: string | null;
  submittedAt: string;
  selected: boolean;
  crewName: string | null;
  crewEmail: string | null;
  /** True for the resolver's synthesized bid at window close — the advertised
   *  crew winning its own listing, never submitted by a person. */
  autoAssigned: boolean;
}

export function fetchBookingBids(
  reference: string,
  signal?: AbortSignal,
): Promise<{ bids: AdminBookingBid[] }> {
  return api<{ bids: AdminBookingBid[] }>({
    path: `/admin/bookings/${encodeURIComponent(reference)}/bids`,
    signal,
  });
}

// Hosts for the match assign dropdown. There is no dedicated /admin/hosts
// endpoint — in this marketplace a "host" is a vendor (assigned_host_id
// references the same users pool), so we reuse the active-vendors list.
export interface AdminHostOption {
  id: number;
  label: string;
  email: string | null;
}

export async function listHosts(signal?: AbortSignal): Promise<AdminHostOption[]> {
  // limit is capped at 100 by the /admin/vendors schema; 200 returned a 400
  // ("Could not load hosts"). The active-vendor pool is small, so 100 is plenty.
  const res = await fetchAdminVendors({ status: 'active', limit: 100 }, signal);
  return res.rows.map((v) => ({
    id: v.id,
    label: v.businessName || v.displayName || v.email,
    email: v.email,
  }));
}

// Cars
export const approveCar = (id: number) => mutate<MutationResponse>(`/admin/cars/${id}/approve`);
export const rejectCar = (id: number, reason?: string) =>
  mutate<MutationResponse>(`/admin/cars/${id}/reject`, { reason });
export const archiveCar = (id: number) => mutate<MutationResponse>(`/admin/cars/${id}/archive`);
export const featureCar = (id: number) => mutate<MutationResponse>(`/admin/cars/${id}/feature`);
export const unfeatureCar = (id: number) =>
  mutate<MutationResponse>(`/admin/cars/${id}/unfeature`);

// Vendors
export interface InviteVendorInput {
  email: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  /** E.164, e.g. +212600000000 — required so bid/booking WhatsApp dispatch works. */
  whatsappPhone: string;
}
export interface InviteVendorResult {
  id: number;
  email: string;
  status: string;
}
/**
 * Invite a vendor: creates a pending, password-less vendor and emails them a
 * link to set their password + finish onboarding. On a duplicate email the
 * backend returns 409 `email_taken`, surfaced as an ApiError (`.body.error`).
 */
export const inviteVendor = (input: InviteVendorInput) =>
  mutate<InviteVendorResult>('/admin/vendors/invite', input);

export interface CreateVendorInput {
  email: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  /** E.164, e.g. +212600000000 — required (bid/booking WhatsApp dispatch). */
  whatsappPhone: string;
  phone?: string;
  /** Portal login password. Omitted → the crew gets a set-password email. */
  password?: string;
  status?: 'active' | 'pending';
}
export interface CreateVendorResult {
  id: number;
  email: string;
  status: string;
  /** True when a password was set — the crew can sign in immediately. */
  loginReady: boolean;
}
/**
 * Create a crew directly — no signup round-trip. Defaults to status=active
 * (immediately biddable + assignable in the host picker), email-verified.
 * 409 `email_taken` on duplicates, same as invite.
 */
export const createVendor = (input: CreateVendorInput) =>
  mutate<CreateVendorResult>('/admin/vendors', input);

/** Flip a pending crew to active without the signup round-trip. */
export const activateVendor = (id: number) =>
  mutate<MutationResponse>(`/admin/vendors/${id}/activate`);

export const approveVendorKyc = (id: number) =>
  mutate<MutationResponse>(`/admin/vendors/${id}/kyc-approve`);
export const rejectVendorKyc = (id: number, reason?: string) =>
  mutate<MutationResponse>(`/admin/vendors/${id}/kyc-reject`, { reason });
export const suspendVendor = (id: number, reason?: string) =>
  mutate<MutationResponse>(`/admin/vendors/${id}/suspend`, { reason });
export const unsuspendVendor = (id: number) =>
  mutate<MutationResponse>(`/admin/vendors/${id}/unsuspend`);

// Crew logins — the PEOPLE who sign in to a crew (app.crew_users), as opposed
// to the crew record itself. A login's email is where their reset link goes;
// it is deliberately separate from the crew's business email (which feeds
// booking notifications), and changing one never touches the other.
export interface AdminCrewLogin {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: 'active' | 'suspended' | string;
  verified: boolean;
  twoFactorEnabled: boolean;
  role: 'owner' | 'manager' | 'staff' | string;
  membershipStatus: string;
  /** ISO time until which failed attempts have locked the login, else null. */
  lockedUntil: string | null;
  /** A reset link is out and neither used nor expired. */
  resetPending: boolean;
  joinedAt: string | null;
}
export function fetchAdminVendorLogins(
  vendorId: number,
  signal?: AbortSignal,
): Promise<{ logins: AdminCrewLogin[] }> {
  return api<{ logins: AdminCrewLogin[] }>({ path: `/admin/vendors/${vendorId}/logins`, signal });
}
export const sendCrewLoginReset = (crewUserId: number) =>
  mutate<{ ok: true; sentTo: string }>(`/admin/crew-users/${crewUserId}/send-reset`);
export const updateCrewLoginEmail = (crewUserId: number, email: string) =>
  api<{ ok: true; id: number; email: string; changed: boolean }>({
    path: `/admin/crew-users/${crewUserId}`,
    method: 'PATCH',
    body: { email },
  });

// Rates sheet on a crew's behalf. The file is base64 in JSON (2 MB cap), the
// same shape the crew app sends; a 422 carries the full report in the body.
export interface VendorSheetReport {
  ok: boolean;
  applied: boolean;
  cars_listed: number;
  changes: { prices: number; seasons: number; tiers: number; blackouts: number };
  errors: Array<{ sheet: string; row: number | null; column: string | null; code: string; message: string }>;
}
export const fetchVendorSheet = (vendorId: number) =>
  apiRaw({ path: `/admin/vendors/${vendorId}/cars/sheet.xlsx` });
export const uploadVendorSheet = (
  vendorId: number,
  body: { file_base64: string; filename?: string; dry_run?: boolean },
) => api<VendorSheetReport>({ path: `/admin/vendors/${vendorId}/cars/sheet`, method: 'POST', body });

// Reviews
export const approveReview = (id: number) =>
  mutate<MutationResponse>(`/admin/reviews/${id}/approve`);
export const rejectReview = (id: number, reason?: string) =>
  mutate<MutationResponse>(`/admin/reviews/${id}/reject`, { reason });
export const spamReview = (id: number) => mutate<MutationResponse>(`/admin/reviews/${id}/spam`);
export const trashReview = (id: number) =>
  mutate<MutationResponse>(`/admin/reviews/${id}/trash`);

export type ReviewBulkAction = 'approve' | 'reject' | 'spam' | 'trash';

export interface ReviewBulkResponse {
  ok: boolean;
  action: ReviewBulkAction;
  processed: number;
  succeeded: number[];
  failed: Array<{ id: number; error: string }>;
}

export function bulkReviewAction(
  ids: number[],
  action: ReviewBulkAction,
  reason?: string,
): Promise<ReviewBulkResponse> {
  return api<ReviewBulkResponse>({
    path: '/admin/reviews/bulk',
    method: 'POST',
    body: { ids, action, reason },
  });
}

export type CarBulkAction = 'approve' | 'reject' | 'archive' | 'feature' | 'unfeature';

export interface CarBulkResponse {
  ok: boolean;
  action: CarBulkAction;
  processed: number;
  succeeded: number[];
  failed: Array<{ id: number; error: string }>;
}

export function bulkCarAction(
  ids: number[],
  action: CarBulkAction,
  reason?: string,
): Promise<CarBulkResponse> {
  return api<CarBulkResponse>({
    path: '/admin/cars/bulk',
    method: 'POST',
    body: { ids, action, reason },
  });
}

export type PayoutBulkAction = 'approve' | 'mark-paid' | 'cancel';

export interface PayoutBulkResponse {
  ok: boolean;
  action: PayoutBulkAction;
  processed: number;
  succeeded: number[];
  failed: Array<{
    id: number;
    error: 'not_found' | 'invalid_transition' | 'unknown';
    from?: string;
  }>;
}

export function bulkPayoutAction(
  ids: number[],
  action: PayoutBulkAction,
  notes?: string,
): Promise<PayoutBulkResponse> {
  return api<PayoutBulkResponse>({
    path: '/admin/payouts/bulk',
    method: 'POST',
    body: { ids, action, notes },
  });
}

export type BookingBulkAction = 'confirm' | 'cancel' | 'complete';

export interface BookingBulkResponse {
  ok: boolean;
  action: BookingBulkAction;
  processed: number;
  succeeded: number[];
  failed: Array<{ id: number; error: string }>;
}

export function bulkBookingAction(
  ids: number[],
  action: BookingBulkAction,
  reason?: string,
): Promise<BookingBulkResponse> {
  return api<BookingBulkResponse>({
    path: '/admin/bookings/bulk',
    method: 'POST',
    body: { ids, action, reason },
  });
}

export type VendorBulkAction =
  | 'kyc-approve'
  | 'kyc-reject'
  | 'suspend'
  | 'unsuspend';

export interface VendorBulkResponse {
  ok: boolean;
  action: VendorBulkAction;
  processed: number;
  succeeded: number[];
  failed: Array<{ id: number; error: string }>;
}

export function bulkVendorAction(
  ids: number[],
  action: VendorBulkAction,
  reason?: string,
): Promise<VendorBulkResponse> {
  return api<VendorBulkResponse>({
    path: '/admin/vendors/bulk',
    method: 'POST',
    body: { ids, action, reason },
  });
}

// ─── Attribute values (lookup for the car attributes picker) ──────

export interface AdminAttributeValueRow {
  id: number;
  slug: string;
  name: string | null;
  position: number | null;
  attributeId: number;
  attributeSlug: string;
  attributeName: string | null;
}

export interface AdminAttributeValuesListResponse {
  rows: AdminAttributeValueRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminAttributeValues(
  params: { q?: string; attributeId?: number; page?: number; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AdminAttributeValuesListResponse> {
  return api<AdminAttributeValuesListResponse>({
    path: `/admin/attribute-values${buildQuery(params)}`,
    signal,
  });
}

// ─── Attribute catalog (full CRUD — /admin/attributes) ───────────
//
// Distinct from the read-only attribute-values picker above. This
// surface drives the /system/attributes editor where admins manage the
// catalog of filterable attributes (vehicle type, transmission, fuel,
// seats, …) and their localized labels.

export type AttributeDisplayType = 'chips' | 'checkboxes' | 'range' | 'toggle';
export type AttributeLocale = 'en' | 'fr' | 'ar' | 'es';

export interface AttributeLocaleFields {
  name: string;
}

export interface AttributeListRow {
  id: number;
  slug: string;
  displayType: AttributeDisplayType;
  showInFilter: boolean | null;
  showInDetail: boolean | null;
  position: number | null;
  updatedAt: string;
  name: string | null;
  valueCount: number;
}

export interface AttributeValueRecord {
  id: number;
  slug: string;
  position: number;
  icon: string | null;
  imageId: number | null;
  /** Resolved media thumbnail/url — read-only; set via uploadAttributeValueImage. */
  imageUrl: string | null;
  /** Avis car-group only: {iso2: "Example Car"} — scraper-owned, read-only. */
  avisExamples: Record<string, string> | null;
  /** Seats and bags for a transfer class. NULL = no stated capacity, which is
   *  a real state — checkout only gates when a number is present. */
  maxPassengers?: number | null;
  maxLuggage?: number | null;
  /** Avis car-group only: vehicle-type crosswalk. `dominant` is editable
   *  (a vehicle-type value slug); byCountry is migration/scraper-derived. */
  mappedVehicleTypes: { dominant?: string | null; byCountry?: Record<string, string> } | null;
  locales: Record<AttributeLocale, AttributeLocaleFields>;
}

export interface AttributeRecord {
  id: number;
  slug: string;
  displayType: AttributeDisplayType;
  showInFilter: boolean | null;
  showInDetail: boolean | null;
  position: number | null;
  updatedAt: string;
  locales: Record<AttributeLocale, AttributeLocaleFields>;
  values: AttributeValueRecord[];
}

export interface AttributeValueDraft {
  id?: number;
  slug: string;
  position: number;
  icon?: string | null;
  imageId?: number | null;
  /** Omit = untouched; null = clear. Merged into mapped_vehicle_types.dominant. */
  dominantVehicleType?: string | null;
  locales: Partial<Record<AttributeLocale, AttributeLocaleFields>>;
}

export interface AttributeSavePayload {
  slug?: string;
  displayType?: AttributeDisplayType;
  showInFilter?: boolean;
  showInDetail?: boolean;
  position?: number;
  locales?: Partial<Record<AttributeLocale, AttributeLocaleFields>>;
  values?: AttributeValueDraft[];
}

// ─── Attribute-value image — direct-to-S3 presign / finalize / delete ─
//
// Sets the category image on an attribute value (e.g. the vehicle-type
// SUV / Sedan art used as the storefront's last-resort listing image).
// Server sets attribute_values.image_id during finalize — callers should
// also patch the value's imageId/imageUrl into their local draft so a
// subsequent full-replacement attribute save doesn't wipe it.

export interface AttributeValueImageFinaliseResponse {
  mediaId: number;
  url: string;
  alt: string | null;
}

export async function uploadAttributeValueImage(
  valueId: number,
  file: File,
): Promise<AttributeValueImageFinaliseResponse> {
  const presign = await api<{ uploadUrl: string; key: string; contentType: string }>({
    path: `/admin/attribute-values/${valueId}/image/presign`,
    method: 'POST',
    body: {
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      originalFilename: file.name,
    },
  });
  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed (${putRes.status} ${putRes.statusText})`);
  }
  return api<AttributeValueImageFinaliseResponse>({
    path: `/admin/attribute-values/${valueId}/image/finalize`,
    method: 'POST',
    body: {
      key: presign.key,
      contentType: presign.contentType,
      sizeBytes: file.size,
      alt: file.name,
    },
  });
}

export function deleteAttributeValueImage(valueId: number): Promise<{ ok: true }> {
  return api({
    path: `/admin/attribute-values/${valueId}/image`,
    method: 'DELETE',
  });
}

// Returns 202 { queued: true } — AI generation takes 40-90s (longer than the
// API gateway allows a response to wait), so the server hands it off to a
// background task. Poll the attribute record until the value's image changes.
export function retrieveAttributeValueImage(valueId: number): Promise<{ queued: boolean }> {
  return api<{ queued: boolean }>({
    path: `/admin/attribute-values/${valueId}/image/retrieve`,
    method: 'POST',
  });
}

export function fetchAttributes(
  params: { q?: string } = {},
  signal?: AbortSignal,
): Promise<{ rows: AttributeListRow[]; total: number }> {
  return api<{ rows: AttributeListRow[]; total: number }>({
    path: `/admin/attributes${buildQuery(params)}`,
    signal,
  });
}

export function fetchAttribute(
  id: number,
  signal?: AbortSignal,
): Promise<AttributeRecord> {
  return api<AttributeRecord>({
    path: `/admin/attributes/${id}`,
    signal,
  });
}

export function createAttribute(payload: {
  slug: string;
  name: string;
  displayType?: AttributeDisplayType;
  showInFilter?: boolean;
  showInDetail?: boolean;
  position?: number;
}): Promise<{ ok: true; id: number }> {
  return api<{ ok: true; id: number }>({
    path: '/admin/attributes',
    method: 'POST',
    body: payload,
  });
}

export function saveAttribute(
  id: number,
  payload: AttributeSavePayload,
): Promise<{ ok: true; id: number }> {
  return api<{ ok: true; id: number }>({
    path: `/admin/attributes/${id}`,
    method: 'PUT',
    body: payload,
  });
}

export function deleteAttribute(id: number): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/attributes/${id}`,
    method: 'DELETE',
  });
}

// ─── Vendor plans (lookup for plan picker on the vendor editor) ───

export interface AdminVendorPlanRow {
  id: number;
  name: string;
  description: string | null;
  status: string | null;
  baseCommission: number | null;
  price: number | null;
  duration: number | null;
  durationType: string | null;
  forRole: string | null;
}

export interface AdminVendorPlansResponse {
  rows: AdminVendorPlanRow[];
  total: number;
}

export function fetchAdminVendorPlans(
  params: { q?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AdminVendorPlansResponse> {
  return api<AdminVendorPlansResponse>({
    path: `/admin/vendor-plans${buildQuery(params)}`,
    signal,
  });
}

// ─── Locations (lookup for relationship pickers + future LocationEditor) ──

export interface AdminLocationRow {
  id: number;
  slug: string;
  type: string;
  countryCode: string | null;
  status: string;
  parentId: number | null;
  name: string | null;
  /** Business top-destinations rank (1 = most important) or null. */
  topRank: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminLocationsListParams {
  q?: string;
  type?: 'country' | 'region' | 'city' | 'airport' | 'station' | 'all';
  countryCode?: string;
  /** Defaults to 'all' server-side — the locations index page finds drafts
   *  there. Pickers that offer a row as a live destination (the partner
   *  "Activated cities" fieldset) ask for 'published' explicitly. */
  status?: 'published' | 'draft' | 'all';
  page?: number;
  /** Capped at 500 by the API, so a picker that must render every published
   *  city at once can ask for the whole list rather than page it. */
  limit?: number;
}

export interface AdminLocationsListResponse {
  rows: AdminLocationRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminLocations(
  params: AdminLocationsListParams = {},
  signal?: AbortSignal,
): Promise<AdminLocationsListResponse> {
  return api<AdminLocationsListResponse>({
    path: `/admin/locations${buildQuery(params)}`,
    signal,
  });
}

/** Distinct countries present in the locations table (lowercase ISO2 +
 *  row count) — feeds the Locations list's country filter. */
export interface AdminLocationCountriesResponse {
  countries: Array<{ code: string; count: number }>;
}

export function fetchAdminLocationCountries(
  signal?: AbortSignal,
): Promise<AdminLocationCountriesResponse> {
  return api<AdminLocationCountriesResponse>({
    path: '/admin/locations/countries',
    signal,
  });
}

// ─── Location create (LocationPicker's Google-fallback auto-create) ──

export interface AdminLocationCreateInput {
  name: string;
  type: 'city' | 'airport' | 'station';
  countryCode: string;
  latitude?: number;
  longitude?: number;
}

export interface AdminLocationCreated {
  id: number;
  slug: string;
  name: string;
  type: string;
  countryCode: string;
  /** Always 'draft' — hidden from the storefront until published. */
  status: string;
}

export function createAdminLocation(
  input: AdminLocationCreateInput,
): Promise<AdminLocationCreated> {
  return api<AdminLocationCreated>({
    path: '/admin/locations',
    method: 'POST',
    body: input,
  });
}

// ─── Google Places proxy (public /places routes on the same Hono host,
//     NOT under /admin — the api() helper takes any path) ─────────────

export interface PlaceSuggestion {
  /** Google placeId. */
  id: string;
  primaryText: string;
  secondaryText: string;
  /** Google place types (e.g. ['airport','establishment']). */
  types: string[];
}

export async function searchPlaces(
  q: string,
  signal?: AbortSignal,
): Promise<PlaceSuggestion[]> {
  const res = await api<{
    suggestions: Array<{
      id: string;
      primaryText: string;
      secondaryText: string;
      types?: string[];
    }>;
  }>({
    path: `/places/autocomplete?q=${encodeURIComponent(q)}`,
    signal,
  });
  return (res.suggestions ?? []).map((s) => ({
    id: s.id,
    primaryText: s.primaryText,
    secondaryText: s.secondaryText,
    types: s.types ?? [],
  }));
}

export interface PlaceDetails {
  latitude: number;
  longitude: number;
  formattedAddress: string | null;
  countryCode: string | null;
}

export function fetchPlaceDetails(
  placeId: string,
  signal?: AbortSignal,
): Promise<PlaceDetails> {
  return api<PlaceDetails>({
    path: `/places/details?placeId=${encodeURIComponent(placeId)}`,
    signal,
  });
}

export type AdminLocationType = 'country' | 'region' | 'city' | 'airport' | 'station';
export type AdminLocationStatus = 'published' | 'draft';
export type AdminLocationCountryCode = 'ma' | 'ae' | 'es' | 'gb' | 'us';

export interface AdminLocationLocaleMap {
  en: string;
  fr: string;
  ar: string;
  es: string;
}

interface AdminLocationMedia {
  id: number;
  filename: string;
  alt: string | null;
  url: string | null;
}

export interface AdminLocationDetail {
  id: number;
  slug: string;
  type: AdminLocationType;
  countryCode: AdminLocationCountryCode | null;
  status: AdminLocationStatus;
  parentId: number | null;
  parent: { id: number; slug: string; name: string | null } | null;
  categoryId: number | null;
  category: { id: number; iconClass: string | null; name: string | null } | null;
  imageId: number | null;
  image: AdminLocationMedia | null;
  bannerId: number | null;
  banner: AdminLocationMedia | null;
  map: { latitude: number | null; longitude: number | null; zoom: number | null };
  /** Business top-destinations rank (1 = most important) or null. Unique
   *  across locations — PATCHing a taken rank returns 409 `rank_taken`. */
  topRank: number | null;
  /** Localised text fields. Read-only — edits go through Payload at
   *  /admin/collections/locations/<id>. */
  localised: {
    name: AdminLocationLocaleMap;
    content: AdminLocationLocaleMap;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LocationPatchInput {
  slug?: string;
  type?: AdminLocationType;
  countryCode?: AdminLocationCountryCode | null;
  status?: AdminLocationStatus;
  topRank?: number | null;
  parentId?: number | null;
  categoryId?: number | null;
  imageId?: number | null;
  bannerId?: number | null;
  map?: {
    latitude?: number | null;
    longitude?: number | null;
    zoom?: number | null;
  };
  /** Per-locale name + description (locations_locales). UPSERTed. */
  locales?: Partial<Record<AttributeLocale, { name?: string; content?: string | null }>>;
}

export function fetchAdminLocationDetail(
  id: number,
  signal?: AbortSignal,
): Promise<AdminLocationDetail> {
  return api<AdminLocationDetail>({ path: `/admin/locations/${id}`, signal });
}

export function patchLocation(
  id: number,
  body: LocationPatchInput,
): Promise<{ ok: true; id: number }> {
  return api({ path: `/admin/locations/${id}`, method: 'PATCH', body });
}

// ─── Location categories (lookup for category picker) ────────────

export interface AdminLocationCategoryRow {
  id: number;
  iconClass: string | null;
  name: string | null;
}

export interface AdminLocationCategoriesResponse {
  rows: AdminLocationCategoryRow[];
  total: number;
}

export function fetchAdminLocationCategories(
  params: { q?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AdminLocationCategoriesResponse> {
  return api<AdminLocationCategoriesResponse>({
    path: `/admin/location-categories${buildQuery(params)}`,
    signal,
  });
}

// ─── Location image + banner — direct-to-S3 presign / finalize / delete ─

export interface LocationMediaFinaliseResponse {
  mediaId: number;
  url: string;
  alt: string | null;
}

interface LocationPresignResponse {
  uploadUrl: string;
  key: string;
  contentType: string;
}

interface LocationPresignInput {
  contentType: string;
  sizeBytes: number;
  originalFilename?: string;
}

function presignLocationSlot(
  locationId: number,
  slot: 'image' | 'banner',
  input: LocationPresignInput,
): Promise<LocationPresignResponse> {
  return api<LocationPresignResponse>({
    path: `/admin/locations/${locationId}/${slot}/presign`,
    method: 'POST',
    body: input,
  });
}

function finaliseLocationSlot(
  locationId: number,
  slot: 'image' | 'banner',
  body: { key: string; contentType: string; sizeBytes: number; alt?: string },
): Promise<LocationMediaFinaliseResponse> {
  return api<LocationMediaFinaliseResponse>({
    path: `/admin/locations/${locationId}/${slot}/finalize`,
    method: 'POST',
    body,
  });
}

function deleteLocationSlot(
  locationId: number,
  slot: 'image' | 'banner',
): Promise<{ ok: true }> {
  return api({
    path: `/admin/locations/${locationId}/${slot}`,
    method: 'DELETE',
  });
}

async function uploadLocationSlot(
  locationId: number,
  slot: 'image' | 'banner',
  file: File,
): Promise<LocationMediaFinaliseResponse> {
  const presign = await presignLocationSlot(locationId, slot, {
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    originalFilename: file.name,
  });
  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed (${putRes.status} ${putRes.statusText})`);
  }
  return finaliseLocationSlot(locationId, slot, {
    key: presign.key,
    contentType: presign.contentType,
    sizeBytes: file.size,
    alt: file.name,
  });
}

export const uploadLocationImage = (id: number, file: File) =>
  uploadLocationSlot(id, 'image', file);
export const uploadLocationBanner = (id: number, file: File) =>
  uploadLocationSlot(id, 'banner', file);
export const deleteLocationImage = (id: number) => deleteLocationSlot(id, 'image');
export const deleteLocationBanner = (id: number) => deleteLocationSlot(id, 'banner');

// ─── User avatars (vendor + customer) — same direct-to-S3 flow ────

export interface AvatarFinaliseResponse {
  mediaId: number;
  url: string;
  alt: string | null;
}

interface AvatarPresignResponse {
  uploadUrl: string;
  key: string;
  contentType: string;
}

interface AvatarPresignInput {
  contentType: string;
  sizeBytes: number;
  originalFilename?: string;
}

function presignUserAvatar(
  base: 'vendors' | 'customers',
  userId: number,
  input: AvatarPresignInput,
): Promise<AvatarPresignResponse> {
  return api<AvatarPresignResponse>({
    path: `/admin/${base}/${userId}/avatar/presign`,
    method: 'POST',
    body: input,
  });
}

function finaliseUserAvatar(
  base: 'vendors' | 'customers',
  userId: number,
  body: { key: string; contentType: string; sizeBytes: number; alt?: string },
): Promise<AvatarFinaliseResponse> {
  return api<AvatarFinaliseResponse>({
    path: `/admin/${base}/${userId}/avatar/finalize`,
    method: 'POST',
    body,
  });
}

function deleteUserAvatar(
  base: 'vendors' | 'customers',
  userId: number,
): Promise<{ ok: true }> {
  return api({ path: `/admin/${base}/${userId}/avatar`, method: 'DELETE' });
}

async function uploadUserAvatar(
  base: 'vendors' | 'customers',
  userId: number,
  file: File,
): Promise<AvatarFinaliseResponse> {
  const presign = await presignUserAvatar(base, userId, {
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    originalFilename: file.name,
  });
  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed (${putRes.status} ${putRes.statusText})`);
  }
  return finaliseUserAvatar(base, userId, {
    key: presign.key,
    contentType: presign.contentType,
    sizeBytes: file.size,
    alt: file.name,
  });
}

export const uploadVendorAvatar = (id: number, file: File) =>
  uploadUserAvatar('vendors', id, file);
export const deleteVendorAvatar = (id: number) => deleteUserAvatar('vendors', id);
export const uploadCustomerAvatar = (id: number, file: File) =>
  uploadUserAvatar('customers', id, file);
export const deleteCustomerAvatar = (id: number) => deleteUserAvatar('customers', id);

// ─── Customers ───────────────────────────────────────────────────────

export interface AdminCustomersListParams {
  status?: 'active' | 'pending' | 'suspended' | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminCustomerRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: string;
  verified: boolean | null;
  createdAt: string;
  avatarUrl: string | null;
  bookingsTotal: number;
  lifetimeValue: number;
  displayName: string;
}

export interface AdminCustomersListResponse {
  rows: AdminCustomerRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminCustomers(
  params: AdminCustomersListParams = {},
  signal?: AbortSignal,
): Promise<AdminCustomersListResponse> {
  return api<AdminCustomersListResponse>({
    path: `/admin/customers${buildQuery(params)}`,
    signal,
  });
}

// ─── Reviews ─────────────────────────────────────────────────────────

export interface AdminReviewsListParams {
  status?: 'approved' | 'pending' | 'rejected' | 'spam' | 'trash' | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminReviewRow {
  id: number;
  rating: number;
  title: string | null;
  body: string | null;
  status: string;
  createdAt: string;
  carId: number;
  customerId: number;
  bookingId: number;
  customerEmail: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  carSlug: string | null;
  carTitle: string | null;
  customerName: string;
}

export interface AdminReviewsListResponse {
  rows: AdminReviewRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminReviews(
  params: AdminReviewsListParams = {},
  signal?: AbortSignal,
): Promise<AdminReviewsListResponse> {
  return api<AdminReviewsListResponse>({
    path: `/admin/reviews${buildQuery(params)}`,
    signal,
  });
}

// ─── Pricing (coupons) ───────────────────────────────────────────────

export interface AdminCouponRow {
  id: number;
  code: string;
  kind: 'fixed' | 'percent';
  value: number;
  startAt: string | null;
  endAt: string | null;
  maxUses: number | null;
  usageCount: number;
  redemptions: number;
  createdAt: string;
}

export interface AdminCouponsListResponse {
  rows: AdminCouponRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminCoupons(signal?: AbortSignal): Promise<AdminCouponsListResponse> {
  return api<AdminCouponsListResponse>({ path: '/admin/pricing/coupons', signal });
}

// ─── Finance ─────────────────────────────────────────────────────────

export interface AdminFinanceResponse {
  gmv30d: number;
  commission30d: number;
  payoutsPending: number;
  payoutsReleased: number;
  walletBalance: number;
  stub?: string;
}

export function fetchAdminFinance(signal?: AbortSignal): Promise<AdminFinanceResponse> {
  return api<AdminFinanceResponse>({ path: '/admin/finance', signal });
}

// ─── Disputes ────────────────────────────────────────────────────────
// Unified backing for the Disputes nav route + the Claims nav route — the
// `group` filter buckets the eight `kind`s into the two nav surfaces.

export type DisputeKind =
  | 'damage'
  | 'refund'
  | 'cleanliness'
  | 'collision'
  | 'theft'
  | 'glass'
  | 'tyre'
  | 'other';

export type DisputeStatus =
  | 'open'
  | 'evidence_pending'
  | 'under_review'
  | 'escalated_to_carrier'
  | 'resolved'
  | 'rejected'
  | 'withdrawn';

export type DisputeResolution =
  | 'refunded'
  | 'partial_refund'
  | 'no_action'
  | 'vendor_charged'
  | 'customer_charged';

export type DisputeFilerRole = 'customer' | 'vendor' | 'admin';

export interface AdminDisputesListParams {
  /** Bucket: `dispute` for the Disputes nav (damage/refund/cleanliness/other),
   *  `claim` for the Claims nav (collision/theft/glass/tyre), `all` for both. */
  group?: 'dispute' | 'claim' | 'all';
  kind?: DisputeKind;
  /** `open_only` is a shortcut for any non-terminal status. */
  status?: DisputeStatus | 'open_only' | 'all';
  assignee?: number;
  unassigned?: boolean;
  booking_id?: number;
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminDisputeRow {
  id: string;
  bookingId: string;
  bookingReference: string | null;
  kind: DisputeKind;
  status: DisputeStatus;
  resolution: DisputeResolution | null;
  filedByUserId: string;
  filedByRole: DisputeFilerRole;
  filedByEmail: string | null;
  filedByName: string | null;
  againstUserId: string;
  againstEmail: string | null;
  assignedAdminId: string | null;
  assignedAdminEmail: string | null;
  title: string;
  amountCents: number | null;
  currency: string | null;
  payoutHold: boolean;
  openedAt: string;
  resolvedAt: string | null;
}

export interface AdminDisputesListResponse {
  rows: AdminDisputeRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminDisputes(
  params: AdminDisputesListParams = {},
  signal?: AbortSignal,
): Promise<AdminDisputesListResponse> {
  return api<AdminDisputesListResponse>({
    path: `/admin/disputes${buildQuery(params)}`,
    signal,
  });
}

export interface AdminDisputeEvent {
  id: string;
  actorUserId: string | null;
  actorRole: 'customer' | 'vendor' | 'admin' | 'system';
  actorEmail: string | null;
  kind:
    | 'message'
    | 'status_change'
    | 'evidence'
    | 'assignment'
    | 'resolution'
    | 'refund_recorded';
  statusFrom: DisputeStatus | null;
  statusTo: DisputeStatus | null;
  body: string | null;
  mediaId: string | null;
  mediaFilename: string | null;
  createdAt: string;
}

export interface AdminDisputeDetail {
  id: string;
  bookingId: string;
  bookingReference: string | null;
  bookingStatus: string | null;
  bookingDateFrom: string | null;
  bookingDateTo: string | null;
  kind: DisputeKind;
  status: DisputeStatus;
  resolution: DisputeResolution | null;
  filedByUserId: string;
  filedByRole: DisputeFilerRole;
  filedByEmail: string | null;
  filedByName: string | null;
  againstUserId: string;
  againstEmail: string | null;
  againstName: string | null;
  assignedAdminId: string | null;
  assignedAdminEmail: string | null;
  title: string;
  description: string;
  amountCents: number | null;
  currency: string | null;
  decidedAmountCents: number | null;
  stripeRefundId: string | null;
  carrier: string | null;
  carrierRef: string | null;
  deductibleCents: number | null;
  payoutHold: boolean;
  openedAt: string;
  resolvedAt: string | null;
  updatedAt: string;
  allowedTransitions: DisputeStatus[];
}

export interface AdminDisputeDetailResponse {
  dispute: AdminDisputeDetail;
  events: AdminDisputeEvent[];
}

export function fetchAdminDispute(
  id: string,
  signal?: AbortSignal,
): Promise<AdminDisputeDetailResponse> {
  return api<AdminDisputeDetailResponse>({ path: `/admin/disputes/${id}`, signal });
}

export interface TransitionDisputeArgs {
  id: string;
  to: DisputeStatus;
  reason?: string;
  resolution?: DisputeResolution;
  decidedAmountCents?: number;
  currency?: string;
  carrier?: string;
  carrierRef?: string;
}

export const transitionDispute = ({ id, ...body }: TransitionDisputeArgs) =>
  mutate<MutationResponse>(`/admin/disputes/${id}/transitions`, body);

export const messageDispute = (id: string, body: string) =>
  mutate<MutationResponse>(`/admin/disputes/${id}/messages`, { body });

export const attachDisputeEvidence = (id: string, mediaId: number, note?: string) =>
  mutate<MutationResponse>(`/admin/disputes/${id}/evidence`, { mediaId, note });

export const assignDispute = (id: string, adminUserId: number | null) =>
  mutate<MutationResponse>(`/admin/disputes/${id}/assign`, { adminUserId });

export const recordDisputeRefund = (id: string, stripeRefundId: string) =>
  mutate<MutationResponse>(`/admin/disputes/${id}/refund-recorded`, { stripeRefundId });

// ─── Complaints ──────────────────────────────────────────────────────
// Support tickets with SLA, priority, and channel. Independent of the
// unified disputes table; complaints have their own conversation surface
// via `app.complaint_events`.

export type ComplaintKind = 'support' | 'fraud' | 'safety' | 'feedback' | 'billing';
export type ComplaintChannel = 'email' | 'chat' | 'whatsapp' | 'phone' | 'web';
export type ComplaintPriority = 'urgent' | 'high' | 'med' | 'low';
export type ComplaintStatus =
  | 'open'
  | 'in_progress'
  | 'breached'
  | 'closed'
  | 'cancelled';

export interface AdminComplaintsListParams {
  status?: ComplaintStatus | 'open_only' | 'overdue' | 'all';
  priority?: ComplaintPriority;
  kind?: ComplaintKind;
  channel?: ComplaintChannel;
  assignee?: number;
  unassigned?: boolean;
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminComplaintRow {
  id: string;
  kind: ComplaintKind;
  fromUserId: string | null;
  fromEmail: string | null;
  fromName: string | null;
  subject: string;
  channel: ComplaintChannel;
  priority: ComplaintPriority;
  assigneeUserId: string | null;
  assigneeEmail: string | null;
  status: ComplaintStatus;
  slaDueAt: string | null;
  openedAt: string;
  closedAt: string | null;
  overdue: boolean;
}

export interface AdminComplaintsListResponse {
  rows: AdminComplaintRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminComplaints(
  params: AdminComplaintsListParams = {},
  signal?: AbortSignal,
): Promise<AdminComplaintsListResponse> {
  return api<AdminComplaintsListResponse>({
    path: `/admin/complaints${buildQuery(params)}`,
    signal,
  });
}

export interface AdminComplaintEvent {
  id: string;
  actorUserId: string | null;
  actorRole: 'reporter' | 'admin' | 'system';
  actorEmail: string | null;
  kind: 'message' | 'status_change' | 'assignment' | 'priority_change' | 'sla_change';
  statusFrom: ComplaintStatus | null;
  statusTo: ComplaintStatus | null;
  priorityFrom: ComplaintPriority | null;
  priorityTo: ComplaintPriority | null;
  body: string | null;
  createdAt: string;
}

export interface AdminComplaintDetail {
  id: string;
  kind: ComplaintKind;
  fromUserId: string | null;
  fromEmail: string | null;
  fromName: string | null;
  subject: string;
  body: string | null;
  channel: ComplaintChannel;
  priority: ComplaintPriority;
  assigneeUserId: string | null;
  assigneeEmail: string | null;
  status: ComplaintStatus;
  slaDueAt: string | null;
  openedAt: string;
  closedAt: string | null;
  overdue: boolean;
  allowedTransitions: ComplaintStatus[];
  /** The trip this complaint is about, resolved server-side. Null for
   *  complaints that carry no booking (billing questions, general support). */
  booking: {
    id: string;
    reference: string | null;
    product: string | null;
    status: string | null;
    dateFrom: string | null;
    pickup: string | null;
    destination: string | null;
    crewName: string | null;
    driverName: string | null;
  } | null;
}

export interface AdminComplaintDetailResponse {
  complaint: AdminComplaintDetail;
  events: AdminComplaintEvent[];
}

export function fetchAdminComplaint(
  id: string,
  signal?: AbortSignal,
): Promise<AdminComplaintDetailResponse> {
  return api<AdminComplaintDetailResponse>({ path: `/admin/complaints/${id}`, signal });
}

export const transitionComplaint = (id: string, to: ComplaintStatus, reason?: string) =>
  mutate<MutationResponse>(`/admin/complaints/${id}/transitions`, { to, reason });

export const messageComplaint = (id: string, body: string) =>
  mutate<MutationResponse>(`/admin/complaints/${id}/messages`, { body });

export const assignComplaint = (id: string, assigneeUserId: number | null) =>
  mutate<MutationResponse>(`/admin/complaints/${id}/assign`, { assigneeUserId });

export const setComplaintPriority = (id: string, priority: ComplaintPriority) =>
  mutate<MutationResponse>(`/admin/complaints/${id}/priority`, { priority });

export const setComplaintSla = (id: string, slaDueAt: string | null, reason?: string) =>
  mutate<MutationResponse>(`/admin/complaints/${id}/sla`, { slaDueAt, reason });

// ─── Reports ─────────────────────────────────────────────────────────

export interface AdminReportTopVendor {
  vendorId: number;
  name: string;
  email: string;
  gmv: number;
  count: number;
}

export interface AdminReportTopCar {
  carId: number;
  slug: string;
  title: string;
  gmv: number;
  count: number;
}

export interface AdminReportFunnel {
  draft: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
}

export interface AdminReportCountry {
  country: string | null;
  gmv: number;
  count: number;
}

export interface AdminReportsResponse {
  monthly: Array<{ month: string; gmv: number; count: number }>;
  topVendors: AdminReportTopVendor[];
  topCars: AdminReportTopCar[];
  funnel: AdminReportFunnel;
  byCountry: AdminReportCountry[];
  since: { last6m: string; last90d: string; last30d: string };
}

export function fetchAdminReports(signal?: AbortSignal): Promise<AdminReportsResponse> {
  return api<AdminReportsResponse>({ path: '/admin/reports', signal });
}

// ─── Settings ────────────────────────────────────────────────────────

export interface AdminSettingsUserRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  status: string;
  twoFactorEnabled: boolean | null;
  createdAt: string;
  displayName: string;
}

export interface AdminSettingsUsersResponse {
  rows: AdminSettingsUserRow[];
  total: number;
}

export function fetchAdminSettingsUsers(
  signal?: AbortSignal,
): Promise<AdminSettingsUsersResponse> {
  return api<AdminSettingsUsersResponse>({ path: '/admin/settings/users', signal });
}

export interface InviteAdminInput {
  email: string;
  firstName?: string;
  lastName?: string;
}
export interface InviteAdminResult {
  id: number;
  email: string;
  role: string;
  status: string;
}
/**
 * Invite an admin: creates a password-less admin and emails a set-password
 * link (ops reset-password flow). 409 `email_taken` if the email exists.
 */
export const inviteAdmin = (input: InviteAdminInput) =>
  mutate<InviteAdminResult>('/admin/settings/users/invite', input);

export interface CreateAdminInput {
  email: string;
  firstName?: string;
  lastName?: string;
  password: string;
}
export interface CreateAdminResult {
  id: number;
  email: string;
  role: string;
  status: string;
  firstName: string | null;
  lastName: string | null;
}
/**
 * Create an admin directly with a password (no email invite). The new admin can
 * sign in immediately. 409 `email_taken` if the email already exists.
 */
export const createAdmin = (input: CreateAdminInput) =>
  mutate<CreateAdminResult>('/admin/settings/users', input);

// The unfiltered /admin/settings/audit reader lived here to feed a 30-row
// preview on the Settings page. That preview is gone — /audit-log is the
// filterable, paginated view of the same data, and EntityActivity shows the
// per-record slice on each detail page — so the client function went with it.
// The endpoint itself still exists.

// ─── Audit log (dedicated page, filterable) ──────────────────────────

export interface AdminAuditLogParams {
  q?: string;
  action?: string;
  actionPrefix?: string;
  entityType?: string;
  entityId?: string;
  actorEmail?: string;
  since?: string;
  until?: string;
  page?: number;
  limit?: number;
}

export interface AdminAuditLogRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  actorEmail: string | null;
  ip: string | null;
  userAgent: string | null;
}

export interface AdminAuditLogListResponse {
  rows: AdminAuditLogRow[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminAuditLogDetail extends AdminAuditLogRow {
  before: unknown;
  after: unknown;
}

export interface AdminAuditLogFacets {
  actionPrefixes: Array<{ prefix: string; count: number }>;
  entityTypes: Array<{ entityType: string; count: number }>;
}

export function fetchAdminAuditLog(
  params: AdminAuditLogParams = {},
  signal?: AbortSignal,
): Promise<AdminAuditLogListResponse> {
  return api<AdminAuditLogListResponse>({
    path: `/admin/audit-log${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminAuditLogFacets(
  signal?: AbortSignal,
): Promise<AdminAuditLogFacets> {
  return api<AdminAuditLogFacets>({ path: '/admin/audit-log/facets', signal });
}

export function fetchAdminAuditLogDetail(
  id: string,
  signal?: AbortSignal,
): Promise<AdminAuditLogDetail> {
  return api<AdminAuditLogDetail>({
    path: `/admin/audit-log/${encodeURIComponent(id)}`,
    signal,
  });
}

// ─── Entity notes (operator notes piggybacked on audit_log) ─────────

export type EntityNoteType =
  | 'bookings'
  | 'cars'
  | 'users'
  | 'locations'
  | 'payouts'
  | 'inquiries'
  | 'reviews'
  | 'coupons';

export function addEntityNote(
  entityType: EntityNoteType,
  entityId: number | string,
  body: string,
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: '/admin/entity-notes',
    method: 'POST',
    body: { entityType, entityId: String(entityId), body },
  });
}

// ─── Cross-entity search (Cmd-K) ─────────────────────────────────────

export interface AdminSearchHit {
  id: number;
  to: string;
  label: string;
  sublabel: string;
  status?: string | null;
}

export interface AdminBookingHit extends AdminSearchHit {
  reference: string;
}

export interface AdminSearchResponse {
  query: string;
  hits: {
    bookings: AdminBookingHit[];
    customers: AdminSearchHit[];
    vendors: AdminSearchHit[];
    cars: AdminSearchHit[];
    locations: AdminSearchHit[];
  };
}

export function fetchAdminSearch(
  q: string,
  signal?: AbortSignal,
): Promise<AdminSearchResponse> {
  return api<AdminSearchResponse>({
    path: `/admin/search${buildQuery({ q })}`,
    signal,
  });
}

// ─── Sidebar attention counts ────────────────────────────────────────

export interface AdminAttentionResponse {
  bookings: number;
  cars: number;
  vendors: number;
  reviews: number;
  inquiries: number;
  support: number;
  payouts: number;
}

export function fetchAdminAttention(
  signal?: AbortSignal,
): Promise<AdminAttentionResponse> {
  return api<AdminAttentionResponse>({ path: '/admin/attention', signal });
}

// ─── System health ───────────────────────────────────────────────────

export type HealthStatus = 'ok' | 'warn' | 'fail';

export type HealthGroup = 'storage' | 'comms' | 'payments' | 'platform';

export interface HealthCheck {
  name: string;
  status: HealthStatus;
  message?: string;
  latencyMs?: number;
  group: HealthGroup;
}

export interface AdminHealthResponse {
  overall: HealthStatus;
  checks: HealthCheck[];
  checkedAt: string;
  environment: string;
}

export function fetchAdminHealth(
  signal?: AbortSignal,
): Promise<AdminHealthResponse> {
  return api<AdminHealthResponse>({ path: '/admin/health', signal });
}

// ─── Inbox ───────────────────────────────────────────────────────────

export interface InboxBookingRow {
  id: number;
  reference: string;
  total: number;
  currency: string;
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  customer: string;
  customerEmail: string;
  car: string | null;
}

export interface InboxVendorKycRow {
  id: number;
  email: string;
  name: string;
  createdAt: string;
}

export interface InboxReviewRow {
  id: number;
  rating: number;
  title: string | null;
  body: string | null;
  createdAt: string;
  customer: string;
  car: string | null;
}

export interface InboxPayoutRow {
  id: number;
  reference: string;
  amount: number;
  currency: string;
  periodFrom: string;
  periodTo: string;
  createdAt: string;
  vendor: string;
}

export interface InboxListingRow {
  id: number;
  slug: string;
  title: string;
  price: number | null;
  createdAt: string;
  vendor: string | null;
}

export interface InboxInquiryRow {
  id: number;
  subject: string;
  updatedAt: string;
  customer: string;
  vendor: string | null;
  car: string | null;
}

export interface AdminInboxResponse {
  sections: {
    bookings: { total: number; rows: InboxBookingRow[] };
    vendorKyc: { total: number; rows: InboxVendorKycRow[] };
    reviews: { total: number; rows: InboxReviewRow[] };
    payouts: { total: number; rows: InboxPayoutRow[] };
    listings: { total: number; rows: InboxListingRow[] };
    inquiries: { total: number; rows: InboxInquiryRow[] };
  };
  checkedAt: string;
}

export function fetchAdminInbox(signal?: AbortSignal): Promise<AdminInboxResponse> {
  return api<AdminInboxResponse>({ path: '/admin/inbox', signal });
}

// ─── Email / SMS templates ───────────────────────────────────────────

export type TemplateLocale = 'en' | 'fr' | 'ar' | 'es';

export type EmailTemplateType =
  | 'booking-confirmation-customer'
  | 'booking-confirmation-vendor'
  | 'booking-confirmation-admin'
  | 'booking-cancelled-customer'
  | 'booking-cancelled-vendor'
  | 'booking-cancelled-admin'
  | 'booking-reminder-customer'
  | 'inquiry-received-vendor'
  | 'inquiry-reply-customer'
  | 'newsletter-welcome';

export type SmsTemplateType =
  | 'booking-confirmation-customer'
  | 'booking-confirmation-vendor'
  | 'booking-confirmation-admin'
  | 'booking-cancelled-customer'
  | 'booking-cancelled-vendor'
  | 'booking-cancelled-admin'
  | 'booking-reminder-customer';

export interface AdminTemplatesIndex {
  email: Array<{ type: EmailTemplateType; locales: TemplateLocale[] }>;
  sms: Array<{ type: SmsTemplateType; locales: TemplateLocale[] }>;
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

export interface RenderedSms {
  body: string;
}

export function fetchAdminTemplatesIndex(
  signal?: AbortSignal,
): Promise<AdminTemplatesIndex> {
  return api<AdminTemplatesIndex>({ path: '/admin/templates', signal });
}

export function previewEmailTemplate(
  type: EmailTemplateType,
  locale: TemplateLocale,
): Promise<RenderedEmail> {
  return api<RenderedEmail>({
    path: `/admin/templates/email/${encodeURIComponent(type)}/preview`,
    method: 'POST',
    body: { locale },
  });
}

export function sendEmailTemplateTest(
  type: EmailTemplateType,
  to: string,
  locale: TemplateLocale,
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/templates/email/${encodeURIComponent(type)}/send-test`,
    method: 'POST',
    body: { to, locale },
  });
}

export function previewSmsTemplate(
  type: SmsTemplateType,
  locale: TemplateLocale,
): Promise<RenderedSms> {
  return api<RenderedSms>({
    path: `/admin/templates/sms/${encodeURIComponent(type)}/preview`,
    method: 'POST',
    body: { locale },
  });
}

export function sendSmsTemplateTest(
  type: SmsTemplateType,
  to: string,
  locale: TemplateLocale,
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/templates/sms/${encodeURIComponent(type)}/send-test`,
    method: 'POST',
    body: { to, locale },
  });
}

// ─── Template editor (load + save) ───────────────────────────────────

export interface EmailLocaleFields {
  subject: string;
  preheader: string;
  bodyHtml: string;
  bodyText: string;
}

export interface SmsLocaleFields {
  body: string;
}

export interface EmailTemplateRecord {
  type: EmailTemplateType;
  exists: boolean;
  enabled: boolean;
  name: string;
  updatedAt: string | null;
  locales: Record<TemplateLocale, EmailLocaleFields>;
}

export interface SmsTemplateRecord {
  type: SmsTemplateType;
  exists: boolean;
  enabled: boolean;
  name: string;
  updatedAt: string | null;
  locales: Record<TemplateLocale, SmsLocaleFields>;
}

export function fetchEmailTemplate(
  type: EmailTemplateType,
  signal?: AbortSignal,
): Promise<EmailTemplateRecord> {
  return api<EmailTemplateRecord>({
    path: `/admin/templates/email/${encodeURIComponent(type)}`,
    signal,
  });
}

export function fetchSmsTemplate(
  type: SmsTemplateType,
  signal?: AbortSignal,
): Promise<SmsTemplateRecord> {
  return api<SmsTemplateRecord>({
    path: `/admin/templates/sms/${encodeURIComponent(type)}`,
    signal,
  });
}

export interface EmailTemplateSavePayload {
  enabled?: boolean;
  name?: string;
  locales?: Partial<Record<TemplateLocale, Partial<EmailLocaleFields>>>;
}

export interface SmsTemplateSavePayload {
  enabled?: boolean;
  name?: string;
  locales?: Partial<Record<TemplateLocale, Partial<SmsLocaleFields>>>;
}

export function saveEmailTemplate(
  type: EmailTemplateType,
  body: EmailTemplateSavePayload,
): Promise<{ ok: true; id: number }> {
  return api<{ ok: true; id: number }>({
    path: `/admin/templates/email/${encodeURIComponent(type)}`,
    method: 'PUT',
    body,
  });
}

export function saveSmsTemplate(
  type: SmsTemplateType,
  body: SmsTemplateSavePayload,
): Promise<{ ok: true; id: number }> {
  return api<{ ok: true; id: number }>({
    path: `/admin/templates/sms/${encodeURIComponent(type)}`,
    method: 'PUT',
    body,
  });
}

// (Reviews mutations — `approveReview` / `rejectReview` / `spamReview` /
// `trashReview` — are exported above alongside the booking + car + vendor
// mutations.)

// ─── Configs ─────────────────────────────────────────────────────────

export interface AdminConfigRow {
  namespace: string;
  value: unknown;
  updatedAt: string | null;
  updatedBy: number | string | null;
}

export interface AdminConfigsListResponse {
  rows: AdminConfigRow[];
  total: number;
}

export function fetchAdminConfigs(
  signal?: AbortSignal,
): Promise<AdminConfigsListResponse> {
  return api<AdminConfigsListResponse>({ path: '/admin/configs', signal });
}

export async function fetchAdminConfig(
  namespace: string,
  signal?: AbortSignal,
): Promise<AdminConfigRow> {
  try {
    return await api<AdminConfigRow>({
      path: `/admin/configs/${encodeURIComponent(namespace)}`,
      signal,
    });
  } catch (err) {
    // No row yet for this namespace (GET returns 404). Treat it as an empty
    // doc so the form renders with defaults and Save creates the row, instead
    // of a hard "Couldn't load namespace: 404". The API still falls back to
    // code defaults for these until a value is saved.
    if ((err as { status?: number })?.status === 404) {
      return { namespace, value: {}, updatedAt: null, updatedBy: null };
    }
    throw err;
  }
}

export interface SaveConfigResponse {
  ok: true;
  namespace: string;
}

export function saveAdminConfig(
  namespace: string,
  value: unknown,
): Promise<SaveConfigResponse> {
  return api<SaveConfigResponse>({
    path: `/admin/configs/${encodeURIComponent(namespace)}`,
    method: 'PUT',
    body: { value },
  });
}

// ─── Currency FX refresh ─────────────────────────────────────────────

export interface FxRefreshEntry {
  code: string;
  symbol: string;
  rateToPrimary?: number;
}

export interface FxRefreshResponse {
  ok: true;
  before: FxRefreshEntry[];
  after: FxRefreshEntry[];
  applied: Array<{ code: string; from?: number; to: number }>;
  skipped: string[];
  flagged: Array<{ code: string; from: number; to: number; pct: number }>;
  fetchedAt: string | null;
}

export function refreshFxRates(confirm = false): Promise<FxRefreshResponse> {
  return api<FxRefreshResponse>({
    method: 'POST',
    path: '/admin/configs/currency-settings/refresh-fx',
    body: { confirm },
  });
}

// ─── Pair-rate matrix seed ────────────────────────────────────────────

export interface SeedPairRatesPreview {
  kind: 'preview';
  additions: Array<{ buy: string; sell: string; rate: number }>;
  kept: number;
}
export interface SeedPairRatesApplied {
  kind: 'applied';
  added: number;
  total: number;
}
export interface SeedPairRatesNoop {
  kind: 'noop';
  kept: number;
}
export type SeedPairRatesResponse = SeedPairRatesPreview | SeedPairRatesApplied | SeedPairRatesNoop;

export function seedPairRates(dryRun: boolean): Promise<SeedPairRatesResponse> {
  return api<SeedPairRatesResponse>({
    method: 'POST',
    path: '/admin/configs/currency-settings/seed-pair-rates',
    body: { dryRun },
  });
}

// ─── SMS test send ──────────────────────────────────────────────────

export interface SmsTestResponse {
  ok: true;
  sid: string;
}

export function testSendSms(input: {
  to: string;
  body: string;
}): Promise<SmsTestResponse> {
  return api<SmsTestResponse>({
    path: '/admin/sms/test',
    method: 'POST',
    body: input,
  });
}

// ─── WhatsApp test send (bid template) ──────────────────────────────

export interface WhatsAppTestVars {
  car: string;
  location: string;
  dates: string;
  price: string;
}

export interface WhatsAppTestResponse {
  ok: true;
  sid: string;
}

export function testSendWhatsApp(input: {
  to: string;
  vars: WhatsAppTestVars;
}): Promise<WhatsAppTestResponse> {
  return api<WhatsAppTestResponse>({
    path: '/admin/whatsapp/test',
    method: 'POST',
    body: input,
  });
}

// Listing-level car media upload/delete client fns were removed with
// CarDetail's ImageManagerSection — photos are DriveCars-managed on the
// catalog entry (see the vehicle-catalog image fns below). The Hono routes in
// apps/api/src/modules/admin/cars-images.ts still exist for scripts/legacy use.

// ─── Email test send + transport echo ───────────────────────────────

export interface EmailTestResponse {
  ok: true;
}

export function testSendEmail(input: {
  to: string;
  subject?: string;
  html?: string;
  locale?: 'en' | 'fr' | 'ar' | 'es';
}): Promise<EmailTestResponse> {
  return api<EmailTestResponse>({
    path: '/admin/email/test',
    method: 'POST',
    body: input,
  });
}

export interface EmailTransportConfig {
  configured: boolean;
  driver: string;
  host: string;
  port: string;
  encryption: string;
  username: string;
  hasPassword: boolean;
  fromAddress: string;
  fromName: string;
}

export function fetchEmailTransportConfig(
  signal?: AbortSignal,
): Promise<EmailTransportConfig> {
  return api<EmailTransportConfig>({ path: '/admin/email/config', signal });
}

// ─── Detail fetches ──────────────────────────────────────────────────

/**
 * The reconciled money on one booking, in the currency the customer was
 * actually charged (API: apps/api/src/modules/bookings/money.ts). The ledger
 * fields on the detail are three currencies deep; this is the single answer.
 */
export interface BookingMoney {
  /** The customer's charge currency — not necessarily the ledger currency. */
  currency: string;
  customerPaid: number;
  crewShare: number;
  partnerCommission: number;
  /** Null until Stripe settled the capture. */
  cardFee: number | null;
  drivecarsNet: number;
  /** False ⇒ every figure is a draft estimate (no crew, no capture yet). */
  settled: boolean;
  /** How this sale earned (0241). A booking made before the snapshot existed
   *  reads 'booking_fee', which is what it was. */
  model?: 'booking_fee' | 'supplier_commission' | 'markup_supplier_commission';
  /** Model 3: what the card captured over the crew's base. 0 otherwise. */
  markup?: number;
  /** Models 2/3: what the CREW owes DriveCars on the base it collected in
   *  cash, billed later through its statement. 0 on the booking fee. */
  supplierCommission?: number;
  /** The partner commission could not be converted into `currency`; it is
   *  counted as 0 above rather than mixed in at the wrong rate. */
  commissionUnconverted?: true;
}

export interface AdminBookingDetail extends AdminBookingRow {
  /** 'car-rentals' | 'airport-transfers'. Rows predating the column read as
   *  'car-rentals', which is what they are. */
  product: string;
  /** 'standard' | 'avis' — an Avis rental is fulfilled by the supplier, not a
   *  crew, so ops can act on it differently. Orthogonal to `product`. */
  bookingType: string;
  /** Where a transfer is going. Null on rentals. */
  destination: string | null;
  /** Which leg of a round trip this is; NULL when one-way. */
  tripLeg: string | null;
  /** THE OTHER LEG. One payment covers both and they cancel together, so ops
   *  fielding a call about one needs to see the other. */
  otherLeg: {
    reference: string;
    tripLeg: string | null;
    dateFrom: string;
    pickupHour: number | null;
    status: string;
    total: number;
    currency: string;
  } | null;
  paymentIntentId: string | null;
  subtotal: number;
  tax: number;
  discount: number;
  walletApplied: number;
  commissionAmount: number;
  pricePerUnit: number;
  systemBasePrice?: number | null;
  /** Bid-settlement money model. authorizedAmount = the 50% hold taken at
   *  checkout; fee = platform fee captured on confirmation; bidPrice = cash to
   *  the crew at pickup; cardFee = Stripe processing fee. */
  authorizedAmount?: number | null;
  advPrice?: number | null;
  bidPrice?: number | null;
  /** Bid lifecycle — 'pending' = no crew bid set yet (booking still matchable). */
  bidStatus?: string | null;
  /** Why the booking is parked at bidStatus 'ready' (0226): 'pick_bid' =
   *  choose the winning bid; 'match_advertised' = fixed-price sale, match a
   *  crew at the advertised price, never above. Null when not parked. */
  bidReadyReason?: 'pick_bid' | 'match_advertised' | null;
  /** Partner selling mode the booking was SOLD under (0225), null = organic. */
  bookingMode?: 'fixed' | 'variable' | null;
  /** Fixed-price sales only: the most the crew base may be, per day, in the
   *  crew's currency — the same number the server's match guard enforces. */
  fixedCeiling?: { perDay: number; currency: string } | null;
  totalPrice?: number | null;
  fee?: number | null;
  cardFee?: number | null;
  /** Card reconciliation (in `owedCurrency` = the buy currency). capturedAmount
   *  is what Stripe actually captured; amountOwed = correct platform fee minus
   *  captured minus paid top-up links. amountOwed > 0 ⇒ collect via Request
   *  payment. Null when not a captured card booking or Stripe was unreachable. */
  capturedAmount?: number | null;
  amountOwed?: number | null;
  owedCurrency?: string | null;
  /** Top-up payments collected (paid Request-payment links) in `owedCurrency`. */
  paidTopUps?: number | null;
  /** Currency the customer transacted in (ledger above is primary/EUR).
   *  purchaseFxRate is rateToPrimary: 1 purchase unit x rate = 1 primary. */
  purchaseCurrency?: string | null;
  purchaseFxRate?: number | null;
  purchaseTotal?: number | null;
  /** The reconciled money — what the customer paid, the crew gets, the partner
   *  earns and DriveCars keeps, all in one currency. */
  money?: BookingMoney;
  /** Payment method selected at draft creation. Drives surcharge math, capture
   *  mode, and refundability copy. */
  paymentMethod: 'card' | 'klarna' | 'crypto';
  /** Snapshot of the processing-fee percentage (e.g. 2.9 for 2.9%) for the
   *  selected payment method at the time the booking was drafted. */
  paymentProcessingFeePct: number;
  /** ISO 4217 code the customer is locked into for this booking's payment. */
  paymentLockedCurrency: string | null;
  /** Locked total in the minor unit of `paymentLockedCurrency` (e.g. cents). */
  paymentLockedAmountMinor: number | null;
  /** ISO timestamp finance stamped to mark host paid out of band (klarna/crypto). */
  hostPayoutCompletedAt: string | null;
  /** ISO timestamp captured when the customer ticked the documentation-
   *  requirements confirmation at cart. Null = legacy row or never acknowledged
   *  (eg. legacy storefront before the requirement landed). */
  documentsAcknowledgedAt: string | null;
  /** Buy-side bid snapshot (T1) — host's negotiated price in `buyCurrency`. */
  bidAmountBuy: number | null;
  /** Currency of `bidAmountBuy` (ISO 4217). */
  buyCurrency: string | null;
  /** Selectable currencies (primary first) for the admin-set-bid form. */
  displayCurrencies?: Array<{ code: string; symbol: string; rateToPrimary: number }>;
  cancellationReason: string | null;
  /** Actual pickup/return instants (ISO string) recorded when the crew confirms
   *  handover. Used for 24h-based billable-day calculation. Null on legacy rows. */
  pickupAt?: string | null;
  returnAt?: string | null;
  /** Pickup/return local wall-clock hour (0-23) + the booking's IANA timezone. */
  pickupHour?: number | null;
  returnHour?: number | null;
  bookingTimezone?: string | null;
  /** Vendor's base currency (car pricing currency, e.g. MAD). Its rate is in
   *  `displayCurrencies`; used to show settlement figures in vendor currency. */
  vendorCurrency?: string | null;
  /** Auto-assign outcome — 'auto_assign' when the advertised crew won at
   *  window close with no lower bid from another local crew; null otherwise. */
  assignedBy: 'auto_assign' | null;
  car: { id: number; slug: string | null; title: string | null };
  location: { slug: string; name: string | null } | null;
  customer: {
    id: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    notes: string | null;
  };
  fulfilment: {
    pickupType: 'delivery' | 'pickup' | null;
    deliveryAddress: string | null;
    deliveryPlaceId: string | null;
    /** The pickup/dropoff location the customer chose at search (airport/station). */
    pickupLocationSlug?: string | null;
    pickupLocationName?: string | null;
  };
  vendor: { id: number; email: string | null; businessName: string | null };
  timeline: Array<{ at: string; event: string }>;
}

export function fetchAdminBookingDetail(
  reference: string,
  signal?: AbortSignal,
): Promise<AdminBookingDetail> {
  return api<AdminBookingDetail>({
    path: `/admin/bookings/${encodeURIComponent(reference)}`,
    signal,
  });
}

export interface AdminCarGalleryItem {
  id: number;
  mediaId: number;
  filename: string | null;
  url: string | null;
  alt: string | null;
  order: number;
}

export type AdminCarStatus = 'draft' | 'pending' | 'published' | 'archived';
export type AdminCarPricingUnit = 'day' | 'hour';
export type AdminCarBookingDefaultState = 'available' | 'unavailable';

export interface AdminCarLocaleMap {
  en: string;
  fr: string;
  ar: string;
  es: string;
}

export interface AdminCarAttributeValue {
  id: number;
  slug: string;
  name: string | null;
  attributeId: number;
  attributeSlug: string;
  attributeName: string | null;
}

/** Per-day rate for stays whose TOTAL length falls in [minDays, maxDays].
 *  maxDays null = open-ended ("8+ days"). Native currency. */
export interface CarDurationTier {
  minDays: number;
  maxDays: number | null;
  pricePerDay: number;
}
/** Per-day rate override for calendar days inside [dateFrom, dateTo]
 *  (YYYY-MM-DD, inclusive). Overlaps: later rows win. Native currency. */
export interface CarPriceException {
  dateFrom: string;
  dateTo: string;
  pricePerDay: number;
  label?: string | null;
}

export interface AdminCarDetail {
  id: number;
  slug: string;
  status: AdminCarStatus;
  isFeatured: boolean;
  /** Canonical display title (Car catalog title, raw-listing fallback). */
  title: string | null;
  /** Raw vendor-entered listing title (cars_locales en). */
  listingTitle?: string | null;
  content: string | null;
  /** Localised text fields. Read-only in the backoffice — edits go through
   *  Payload at /admin/collections/cars/<id>. Returned here so the editor can
   *  show e.g. the EN title as a header for orientation. */
  localised: {
    title: AdminCarLocaleMap;
    content: AdminCarLocaleMap;
    seoMetaTitle: AdminCarLocaleMap;
    seoMetaDescription: AdminCarLocaleMap;
  };
  /** Linked vehicle_catalog_entries.id — null on legacy unlinked listings. */
  carId: number | null;
  /** Canonical Car copy per locale. The storefront falls back to these when
   *  the listing's own localised fields are empty; the editor surfaces them
   *  as "inherited" hints. Null when no catalog entry is linked. */
  catalogLocales: Partial<Record<'en' | 'fr' | 'ar' | 'es', CatalogLocaleCopy>> | null;
  pricing: {
    price: number;
    salePrice: number | null;
    unit: AdminCarPricingUnit;
    securityDeposit: number | null;
    currency: string;
    /** Per-listing minimum rental length in days; null = platform floor only. */
    minDays: number | null;
  };
  /** Variable pricing (rates in the listing's native currency). */
  durationTiers: CarDurationTier[];
  priceExceptions: CarPriceException[];
  number: number | null;
  reviewCount: number;
  reviewScore: number | null;
  address: string | null;
  map: { latitude: number | null; longitude: number | null; zoom: number | null } | null;
  booking: { instantBooking: boolean; defaultState: AdminCarBookingDefaultState | null };
  youtubeUrl: string | null;
  icalImportUrl: string | null;
  icalExportUrl: string | null;
  extraInfo: {
    passenger: string | null;
    gearShift: string | null;
    baggage: string | null;
    doors: string | null;
  };
  seoShareImageId: number | null;
  location: { slug: string; name: string | null } | null;
  locationId: number | null;
  vendor: {
    id: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
  };
  vendorId: number;
  imageId: number | null;
  imageUrl: string | null;
  gallery: AdminCarGalleryItem[];
  attributeValues: AdminCarAttributeValue[];
  /** Airports/stations this car can be picked up from (car_pickup_locations). */
  pickupAirports: AdminCarPickupAirport[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminCarPickupAirport {
  id: number;
  slug: string;
  name: string;
  type: string | null;
}

// Edit payload — only non-localised columns. Localised content stays in the
// CMS by design. Mirrors `CarPatchSchema` in apps/api/src/modules/admin/cars.ts.
export interface CarPatchInput {
  slug?: string;
  status?: AdminCarStatus;
  isFeatured?: boolean;
  number?: number;
  address?: string | null;
  youtubeUrl?: string | null;
  icalImportUrl?: string | null;
  icalExportUrl?: string | null;
  vendorId?: number;
  locationId?: number;
  seoShareImageId?: number | null;
  map?: {
    latitude?: number | null;
    longitude?: number | null;
    zoom?: number | null;
  };
  pricing?: {
    price?: number;
    salePrice?: number | null;
    unit?: AdminCarPricingUnit;
    securityDeposit?: number | null;
    currency?: string;
    minDays?: number | null;
  };
  booking?: {
    instantBooking?: boolean;
    defaultState?: AdminCarBookingDefaultState | null;
  };
  extraInfo?: {
    passenger?: string | null;
    gearShift?: string | null;
    baggage?: string | null;
    doors?: string | null;
  };
  /** Full replacement set. Pass [] to clear all. */
  attributeValueIds?: number[];
  /** Full replacement set of pickup airport/station location ids. Pass [] to clear. */
  pickupAirportIds?: number[];
  /** Variable pricing — full replacement sets. Pass [] to clear. */
  durationTiers?: CarDurationTier[];
  priceExceptions?: CarPriceException[];
  /** Per-locale title / description / SEO meta (cars_locales). UPSERTed. */
  locales?: Partial<
    Record<
      AttributeLocale,
      {
        title?: string;
        content?: string | null;
        seoMetaTitle?: string | null;
        seoMetaDescription?: string | null;
      }
    >
  >;
}

export function patchCar(id: number, body: CarPatchInput): Promise<{ ok: true; id: number }> {
  return api({ path: `/admin/cars/${id}`, method: 'PATCH', body });
}

/** City-specific AI copy for a listing (gpt-4o): fills ONLY the empty
 *  description/SEO fields per locale, persisted server-side. 503 →
 *  ApiError with body.error === 'ai_not_configured'. Also runs
 *  automatically on approve for copy-less listings. */
export function retrieveCarCopy(id: number): Promise<{ ok: true; filled: string[] }> {
  return api({ path: `/admin/cars/${id}/retrieve/copy`, method: 'POST' });
}

export function fetchAdminCarDetail(id: number, signal?: AbortSignal): Promise<AdminCarDetail> {
  return api<AdminCarDetail>({ path: `/admin/cars/${id}`, signal });
}

export type AdminVendorStatus = 'active' | 'pending' | 'suspended';
export type AdminVendorVerificationStatus =
  | 'approved'
  | 'pending'
  | 'rejected'
  | 'unverified';
export type AdminVendorCommissionType = 'default' | 'percent' | 'fixed';

interface AdminAvatarMedia {
  id: number;
  filename: string;
  alt: string | null;
  url: string | null;
}

export interface AdminVendorDetail {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: AdminVendorStatus;
  verificationStatus: AdminVendorVerificationStatus | null;
  createdAt: string;
  businessName: string | null;
  businessAddress: string | null;
  taxId: string | null;
  commissionType: AdminVendorCommissionType | null;
  commissionValue: number | null;
  /** Per-crew pricing overrides (fractions; null = use global default). */
  vehicleMarkupPct: number | null;
  platformFeePct: number | null;
  /** The commission this crew agreed to pay DriveCars on the base it collects
   *  at pickup, when a partner sells it on a supplier-commission model (0241).
   *  A fraction, 0..1. null = no rate agreed, which HIDES the crew from those
   *  partners — never the same as 0, which means "agreed zero, still sold". */
  supplierCommissionPct: number | null;
  /** Bid-invite notification channel + WhatsApp number for this fleet. */
  notificationChannel: 'whatsapp' | 'email' | 'both';
  whatsappPhone: string | null;
  /** Pickup point for the results map (one pin per vendor). */
  pickupLatitude: number | null;
  pickupLongitude: number | null;
  planId: number | null;
  planName: string | null;
  avatarId: number | null;
  avatar: AdminAvatarMedia | null;
  carsTotal: number;
  bookingsTotal: number;
  /** True for the platform's own house crew — has no other bidder to lose to,
   *  so auto-assign is meaningless for it. */
  isHouse: boolean;
  /** e.g. 'avis' for the Avis supplier row — not a crew, so auto-assign is
   *  refused for it too. Null for an ordinary local crew. */
  source: string | null;
  /** Ops toggle: this crew wins its own advertised bookings unless a lower
   *  bid arrives from another local crew. Only editable for a local crew
   *  (`!isHouse && source !== 'avis'`). */
  autoAssign: boolean;
  /** Whether this crew committed to honouring its advertised prices — set
   *  from the crew app, gates visibility on fixed-mode partners. Ops may
   *  only clear it (VendorPatchInput.fixedPriceOptin below), never set it. */
  fixedPriceOptin: boolean;
  fixedPriceOptinAt: string | null;
  /** Daily re-attestation stamp; "today" is the UTC calendar day, matching
   *  the API's CURRENT_DATE comparison. */
  pricesConfirmedAt: string | null;
  /** Caches + admin metadata only — editorial fields source from cms.users above. */
  profile: {
    payoutsTotalCents: number;
    responseTimeSeconds: number | null;
    flagged: boolean;
    notes: string | null;
    tier: string | null;
  } | null;
}

export interface VendorPatchInput {
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  status?: AdminVendorStatus;
  verificationStatus?: AdminVendorVerificationStatus;
  businessName?: string | null;
  businessAddress?: string | null;
  taxId?: string | null;
  commissionType?: AdminVendorCommissionType | null;
  commissionValue?: number | null;
  vehicleMarkupPct?: number | null;
  platformFeePct?: number | null;
  /** 0..1, or null to clear the agreed rate (which hides the crew from
   *  supplier-commission partners). */
  supplierCommissionPct?: number | null;
  notificationChannel?: 'whatsapp' | 'email' | 'both';
  whatsappPhone?: string | null;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  planId?: number | null;
  /** Admin metadata — written to app.vendor_profiles, not cms.users. */
  flagged?: boolean;
  notes?: string | null;
  tier?: string | null;
  /** Refused (400 auto_assign_not_allowed) for the house crew and the Avis
   *  supplier — local crews only. */
  autoAssign?: boolean;
  /** Only `false` is accepted here — clears the crew's opt-in (and its
   *  stamps). The opt-in itself is the crew's own commitment, set from the
   *  crew app; `true` from this admin surface is refused server-side
   *  (400 fixed_price_optin_is_crew_choice). */
  fixedPriceOptin?: false;
}

export function patchVendor(
  id: number,
  body: VendorPatchInput,
): Promise<{ ok: true; id: number }> {
  return api({ path: `/admin/vendors/${id}`, method: 'PATCH', body });
}

export function fetchAdminVendorDetail(
  id: number,
  signal?: AbortSignal,
): Promise<AdminVendorDetail> {
  return api<AdminVendorDetail>({ path: `/admin/vendors/${id}`, signal });
}

export interface AdminCustomerDetail {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: string;
  verified: boolean;
  createdAt: string;
  avatarId: number | null;
  avatar: AdminAvatarMedia | null;
  bookingsTotal: number;
  recentBookings: Array<{
    id: number;
    reference: string;
    status: string;
    total: number;
    currency: string;
    dateFrom: string;
    dateTo: string;
    createdAt: string;
    carTitle: string | null;
    carSlug: string | null;
  }>;
  /** Caches + admin metadata only — editorial fields source from cms.users above. */
  profile: {
    flagged: boolean;
    lastActiveAt: string | null;
    lifetimeRating: number | null;
    totalSpentCents: number;
    tripsCount: number;
  } | null;
}

export type AdminCustomerStatus = 'active' | 'pending' | 'suspended';

export interface CustomerPatchInput {
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  status?: AdminCustomerStatus;
  verified?: boolean;
  /** Admin metadata — written to app.customer_profiles, not cms.users. */
  flagged?: boolean;
}

export function patchCustomer(
  id: number,
  body: CustomerPatchInput,
): Promise<{ ok: true; id: number }> {
  return api({ path: `/admin/customers/${id}`, method: 'PATCH', body });
}

export function suspendCustomer(id: number, reason?: string): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/customers/${id}/suspend`,
    method: 'POST',
    body: { reason },
  });
}

export function unsuspendCustomer(id: number): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/customers/${id}/unsuspend`,
    method: 'POST',
  });
}

export function fetchAdminCustomerDetail(
  id: number,
  signal?: AbortSignal,
): Promise<AdminCustomerDetail> {
  return api<AdminCustomerDetail>({ path: `/admin/customers/${id}`, signal });
}

// ─── Customer wallet ─────────────────────────────────────────────────

export type WalletTxnType = 'topup' | 'debit' | 'refund' | 'adjust';

export interface AdminWalletTransaction {
  reference: string;
  type: WalletTxnType;
  amount: number;
  currency: string;
  balanceAfter: number;
  bookingId: number | null;
  paymentIntentId: string | null;
  note: string | null;
  createdAt: string;
}

export interface AdminWalletSnapshot {
  balance: number;
  currency: string;
  transactions: AdminWalletTransaction[];
}

export function fetchAdminCustomerWallet(
  id: number,
  signal?: AbortSignal,
): Promise<AdminWalletSnapshot> {
  return api<AdminWalletSnapshot>({
    path: `/admin/customers/${id}/wallet`,
    signal,
  });
}

export interface AdjustWalletInput {
  direction: 'credit' | 'debit';
  amount: number;
  note: string;
}

export interface AdjustWalletResponse {
  ok: true;
  id: number;
  balanceAfter: number;
  reference: string;
}

export function adjustCustomerWallet(
  id: number,
  body: AdjustWalletInput,
): Promise<AdjustWalletResponse> {
  return api<AdjustWalletResponse>({
    path: `/admin/customers/${id}/wallet/adjust`,
    method: 'POST',
    body,
  });
}

// ─── Payouts ─────────────────────────────────────────────────────────

export type PayoutStatus = 'requested' | 'approved' | 'paid' | 'cancelled';

export interface AdminPayoutVendor {
  id: number;
  email: string;
  name: string;
}

export interface AdminPayoutRow {
  id: number;
  reference: string;
  amount: number;
  currency: string;
  status: PayoutStatus;
  periodFrom: string;
  periodTo: string;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: AdminPayoutVendor;
}

export interface AdminPayoutsListParams {
  status?: PayoutStatus | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminPayoutsListResponse {
  rows: AdminPayoutRow[];
  total: number;
  statusCounts: Record<string, number>;
  statusTotals: Record<string, number>;
  page: number;
  limit: number;
}

export interface AdminPayoutBookingRow {
  id: number;
  reference: string;
  status: string;
  total: number;
  commission: number;
  currency: string;
  dateFrom: string;
  dateTo: string;
  carTitle: string | null;
}

export interface AdminPayoutDetail extends AdminPayoutRow {
  bookings: AdminPayoutBookingRow[];
}

export function fetchAdminPayouts(
  params: AdminPayoutsListParams = {},
  signal?: AbortSignal,
): Promise<AdminPayoutsListResponse> {
  return api<AdminPayoutsListResponse>({
    path: `/admin/payouts${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminPayout(
  id: number,
  signal?: AbortSignal,
): Promise<AdminPayoutDetail> {
  return api<AdminPayoutDetail>({ path: `/admin/payouts/${id}`, signal });
}

export function approvePayout(id: number): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/payouts/${id}/approve`,
    method: 'POST',
  });
}

export function markPayoutPaid(
  id: number,
  options: { paidAt?: string; notes?: string } = {},
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/payouts/${id}/mark-paid`,
    method: 'POST',
    body: options,
  });
}

export function cancelPayout(
  id: number,
  options: { notes?: string } = {},
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/payouts/${id}/cancel`,
    method: 'POST',
    body: options,
  });
}

export interface AdminVendorPayoutRow {
  id: number;
  reference: string;
  amount: number;
  currency: string;
  status: PayoutStatus;
  periodFrom: string;
  periodTo: string;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
}

export function fetchAdminVendorPayouts(
  vendorId: number,
  signal?: AbortSignal,
): Promise<{ rows: AdminVendorPayoutRow[] }> {
  return api({ path: `/admin/vendors/${vendorId}/payouts`, signal });
}

// ─── Coupons CRUD ────────────────────────────────────────────────────

export interface AdminCouponDetail {
  id: number;
  code: string;
  kind: 'fixed' | 'percent';
  value: number;
  currency: string | null;
  enabled: boolean;
  startAt: string | null;
  endAt: string | null;
  maxUses: number | null;
  usageCount: number;
  perUserLimit: number | null;
  minTotal: number | null;
  isVendor: boolean;
  redemptions?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCouponsListParams {
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminCouponsListResponse2 {
  rows: AdminCouponDetail[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminCouponsList(
  params: AdminCouponsListParams = {},
  signal?: AbortSignal,
): Promise<AdminCouponsListResponse2> {
  return api<AdminCouponsListResponse2>({
    path: `/admin/coupons${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminCouponDetail(
  id: number,
  signal?: AbortSignal,
): Promise<AdminCouponDetail> {
  return api<AdminCouponDetail>({ path: `/admin/coupons/${id}`, signal });
}

export interface CouponWriteInput {
  code: string;
  kind: 'fixed' | 'percent';
  value: number;
  currency?: string;
  enabled?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  usageLimit?: number | null;
  perUserLimit?: number | null;
  minTotal?: number | null;
}

export function createCoupon(data: CouponWriteInput): Promise<MutationResponse> {
  return api<MutationResponse>({ path: '/admin/coupons', method: 'POST', body: data });
}

export function updateCoupon(
  id: number,
  data: Partial<CouponWriteInput>,
): Promise<MutationResponse> {
  return api<MutationResponse>({ path: `/admin/coupons/${id}`, method: 'PATCH', body: data });
}

export function archiveCoupon(id: number): Promise<MutationResponse> {
  return api<MutationResponse>({ path: `/admin/coupons/${id}`, method: 'DELETE' });
}

// ─── Support / Contact submissions ───────────────────────────────────

export interface AdminSupportListParams {
  status?: 'new' | 'in_progress' | 'resolved' | 'spam' | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminSupportRow {
  id: number;
  name: string | null;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  status: string;
  source: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSupportListResponse {
  rows: AdminSupportRow[];
  total: number;
  statusCounts: Record<string, number>;
  page: number;
  limit: number;
}

export function fetchAdminSupport(
  params: AdminSupportListParams = {},
  signal?: AbortSignal,
): Promise<AdminSupportListResponse> {
  return api<AdminSupportListResponse>({
    path: `/admin/support${buildQuery(params)}`,
    signal,
  });
}

export function setSupportStatus(
  id: number,
  status: 'new' | 'in_progress' | 'resolved' | 'spam',
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/support/${id}/status`,
    method: 'PATCH',
    body: { status },
  });
}

// ─── Partner applications ────────────────────────────────────────────

export type PartnerApplicationStatus = 'new' | 'in_review' | 'approved' | 'rejected';

export interface AdminPartnerApplicationListParams {
  status?: PartnerApplicationStatus | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminPartnerApplicationRow {
  id: number;
  companyName: string;
  website: string | null;
  country: string;
  partnerType: string | null;
  contactName: string;
  email: string;
  phone: string;
  message: string | null;
  status: string;
  source: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPartnerApplicationListResponse {
  rows: AdminPartnerApplicationRow[];
  total: number;
  statusCounts: Record<string, number>;
  page: number;
  limit: number;
}

export function fetchAdminPartnerApplications(
  params: AdminPartnerApplicationListParams = {},
  signal?: AbortSignal,
): Promise<AdminPartnerApplicationListResponse> {
  return api<AdminPartnerApplicationListResponse>({
    path: `/admin/partner-applications${buildQuery(params)}`,
    signal,
  });
}

export function setPartnerApplicationStatus(
  id: number,
  status: PartnerApplicationStatus,
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/partner-applications/${id}/status`,
    method: 'PATCH',
    body: { status },
  });
}

// ─── Inquiries ───────────────────────────────────────────────────────

export type InquiryStatus = 'open' | 'replied' | 'closed';

export interface AdminInquiriesListParams {
  status?: InquiryStatus | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminInquiryParty {
  id: number;
  email: string;
  name: string;
}

export interface AdminInquiryCar {
  id: number;
  title: string | null;
  slug: string | null;
}

export interface AdminInquiryRow {
  id: number;
  subject: string;
  status: InquiryStatus;
  createdAt: string;
  updatedAt: string;
  customer: AdminInquiryParty;
  vendor: AdminInquiryParty;
  car: AdminInquiryCar;
  contact: { email: string; name: string | null };
  messageCount: number;
}

export interface AdminInquiriesListResponse {
  rows: AdminInquiryRow[];
  total: number;
  statusCounts: Record<string, number>;
  page: number;
  limit: number;
}

export interface AdminInquiryMessage {
  id: string;
  order: number;
  sender: 'customer' | 'vendor' | 'admin';
  body: string;
  sentAt: string;
  senderUserId: number | null;
  senderName: string | null;
}

export interface AdminInquiryDetail {
  id: number;
  subject: string;
  status: InquiryStatus;
  createdAt: string;
  updatedAt: string;
  customer: AdminInquiryParty;
  vendor: AdminInquiryParty;
  car: AdminInquiryCar;
  contact: {
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
  messages: AdminInquiryMessage[];
}

export function fetchAdminInquiries(
  params: AdminInquiriesListParams = {},
  signal?: AbortSignal,
): Promise<AdminInquiriesListResponse> {
  return api<AdminInquiriesListResponse>({
    path: `/admin/inquiries${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminInquiry(
  id: number,
  signal?: AbortSignal,
): Promise<AdminInquiryDetail> {
  return api<AdminInquiryDetail>({ path: `/admin/inquiries/${id}`, signal });
}

export function setInquiryStatus(
  id: number,
  status: InquiryStatus,
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/inquiries/${id}/status`,
    method: 'PATCH',
    body: { status },
  });
}

export function replyToInquiry(
  id: number,
  body: string,
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/inquiries/${id}/reply`,
    method: 'POST',
    body: { body },
  });
}

// ─── Newsletter subscribers ──────────────────────────────────────────

export type SubscriberStatus = 'subscribed' | 'unsubscribed' | 'bounced';

export interface AdminNewsletterListParams {
  status?: SubscriberStatus | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminNewsletterRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: SubscriberStatus;
  source: string | null;
  consent: boolean;
  createdAt: string;
  updatedAt: string;
  unsubscribedAt: string | null;
}

export interface AdminNewsletterListResponse {
  rows: AdminNewsletterRow[];
  total: number;
  statusCounts: Record<string, number>;
  page: number;
  limit: number;
}

export function fetchAdminNewsletter(
  params: AdminNewsletterListParams = {},
  signal?: AbortSignal,
): Promise<AdminNewsletterListResponse> {
  return api<AdminNewsletterListResponse>({
    path: `/admin/newsletter${buildQuery(params)}`,
    signal,
  });
}

export function setNewsletterStatus(
  id: number,
  status: SubscriberStatus,
): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/newsletter/${id}/status`,
    method: 'PATCH',
    body: { status },
  });
}

export function deleteNewsletterSubscriber(id: number): Promise<MutationResponse> {
  return api<MutationResponse>({
    path: `/admin/newsletter/${id}`,
    method: 'DELETE',
  });
}

export interface NewsletterImportResponse {
  dryRun: boolean;
  totalRows: number;
  inserted?: number;
  updated?: number;
  wouldInsertOrUpdate?: number;
  errors: Array<{ row: number; reason: string; raw?: string }>;
}

export function importNewsletterCsv(
  csv: string,
  options: { dryRun?: boolean } = {},
): Promise<NewsletterImportResponse> {
  return api<NewsletterImportResponse>({
    path: '/admin/newsletter/import',
    method: 'POST',
    body: { csv, dryRun: options.dryRun },
  });
}

/**
 * Streams a CSV from an admin endpoint and triggers a browser download.
 * JWT must travel in a header so we can't use a plain anchor — fetch as a
 * blob and use a synthetic anchor to save. Filename comes from the server's
 * Content-Disposition header when present, falling back to the caller's
 * default.
 */
export async function downloadAdminCsv(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const res = await apiRaw({ path });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadNewsletterCsv(
  status: SubscriberStatus | 'all' = 'subscribed',
): Promise<void> {
  return downloadAdminCsv(
    `/admin/newsletter/export${buildQuery({ status })}`,
    `drivecars-newsletter-${status}-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export function downloadBookingsCsv(
  params: Pick<AdminBookingsListParams, 'status' | 'paymentMethod' | 'q'> = {},
): Promise<void> {
  const status = params.status ?? 'all';
  const paymentMethod = params.paymentMethod ?? 'all';
  return downloadAdminCsv(
    `/admin/bookings/export.csv${buildQuery({ status, paymentMethod, q: params.q })}`,
    `drivecars-bookings-${status}-${todayIso()}.csv`,
  );
}

export function downloadCustomersCsv(
  params: { status?: AdminCustomerStatus | 'all'; q?: string } = {},
): Promise<void> {
  const status = params.status ?? 'all';
  return downloadAdminCsv(
    `/admin/customers/export.csv${buildQuery({ status, q: params.q })}`,
    `drivecars-customers-${status}-${todayIso()}.csv`,
  );
}

export function downloadVendorsCsv(
  params: {
    status?: AdminVendorStatus | 'all';
    verification?: AdminVendorVerificationStatus | 'all';
    q?: string;
  } = {},
): Promise<void> {
  const status = params.status ?? 'all';
  return downloadAdminCsv(
    `/admin/vendors/export.csv${buildQuery({
      status,
      verification: params.verification,
      q: params.q,
    })}`,
    `drivecars-vendors-${status}-${todayIso()}.csv`,
  );
}

export function downloadPayoutsCsv(
  params: Pick<AdminPayoutsListParams, 'status' | 'q'> = {},
): Promise<void> {
  const status = params.status ?? 'all';
  return downloadAdminCsv(
    `/admin/payouts/export.csv${buildQuery({ status, q: params.q })}`,
    `drivecars-payouts-${status}-${todayIso()}.csv`,
  );
}

export function downloadCarsCsv(
  params: {
    status?: string;
    q?: string;
    countryCode?: string;
    locationId?: number;
    testCrew?: AdminCarsTestCrewFilter;
  } = {},
): Promise<void> {
  const status = params.status ?? 'all';
  const testCrew = params.testCrew ?? 'exclude';
  return downloadAdminCsv(
    `/admin/cars/export.csv${buildQuery({ status, q: params.q, countryCode: params.countryCode, locationId: params.locationId, testCrew })}`,
    `drivecars-cars-${status}${testCrew === 'only' ? '-test' : testCrew === 'all' ? '-incl-test' : ''}-${todayIso()}.csv`,
  );
}

export function downloadConfigsCsv(): Promise<void> {
  return downloadAdminCsv('/admin/configs/export.csv', `drivecars-configs-${todayIso()}.csv`);
}

export interface CreateCarInput {
  /** Catalog entry id (vehicle_catalog_entries.id) — the canonical Car. Required. */
  carId: number;
  /** ISO 3166-1 alpha-2 (lowercased server-side). Required. */
  countryCode: string;
  /** One of the 5 configured display currencies. Defaults from the country server-side. */
  currency?: string;
  /** Pricing unit; defaults to 'day' server-side. */
  unit?: 'day' | 'hour';
  /** Optional — defaults to the catalog entry's default title. */
  title?: string;
  slug?: string;
  vendorId: number;
  /** Optional — a country-only listing has no pickup location yet. */
  locationId?: number;
  price: number;
  /** Per-listing minimum rental length in days. Optional; platform floor still applies. */
  minDays?: number;
  status?: 'draft' | 'pending' | 'published' | 'archived';
}
export interface CreateCarResult {
  id: number;
  slug: string;
  status: string;
}
/**
 * Create a catalog-based car listing (admin). `carId` (catalog entry) + country
 * are required; location is optional. 409 `slug_taken` if the slug collides;
 * 400 `catalog_entry_not_found` / `vendor_not_found` / `location_not_found`.
 */
export const createCar = (input: CreateCarInput) =>
  mutate<CreateCarResult>('/admin/cars', input);

// ─── Catalog picker (make → model → year → entry) ────────────────────
//
// Admin-auth mirror of the crew /vendor/catalog reads, powering the
// new-listing modal's CatalogPicker. Each fetcher unwraps the Hono
// `{ makes | models | years | entries }` envelope to the bare array so the
// TanStack Query callers get the collection directly.
//
// NB: distinct from the fetchCatalog* CRUD helpers below (which drive the
// /admin/vehicle-catalog management screens) — these hit the lean read-only
// /admin/catalog/* endpoints and are named `*Picker*` to avoid the clash.

export interface CatalogMake {
  id: number;
  slug: string;
  name: string;
}

export interface CatalogModel {
  id: number;
  slug: string;
  name: string;
}

export interface CatalogEntrySummary {
  id: number;
  trim: string;
  slug: string;
  /** The canonical Car's display title (server maps `default_title` → camelCase). */
  defaultTitle: string;
}

export function fetchCatalogPickerMakes(signal?: AbortSignal): Promise<CatalogMake[]> {
  return api<{ makes: CatalogMake[] }>({ path: '/admin/catalog/makes', signal }).then(
    (r) => r.makes,
  );
}

export function fetchCatalogPickerModels(
  makeId: number,
  signal?: AbortSignal,
): Promise<CatalogModel[]> {
  return api<{ models: CatalogModel[] }>({
    path: `/admin/catalog/makes/${makeId}/models`,
    signal,
  }).then((r) => r.models);
}

export function fetchCatalogPickerYears(
  modelId: number,
  signal?: AbortSignal,
): Promise<number[]> {
  return api<{ years: number[] }>({
    path: `/admin/catalog/models/${modelId}/years`,
    signal,
  }).then((r) => r.years);
}

export function fetchCatalogPickerEntries(
  modelId: number,
  year: number | null,
  signal?: AbortSignal,
): Promise<CatalogEntrySummary[]> {
  return api<{ entries: CatalogEntrySummary[] }>({
    path: `/admin/catalog/entries${buildQuery({ modelId, year })}`,
    signal,
  }).then((r) => r.entries);
}

export function downloadFinanceCsv(): Promise<void> {
  return downloadAdminCsv('/admin/finance/export.csv', `drivecars-finance-${todayIso()}.csv`);
}

export function downloadDashboardCsv(): Promise<void> {
  return downloadAdminCsv('/admin/dashboard/export.csv', `drivecars-dashboard-gmv-${todayIso()}.csv`);
}

export function downloadReportsCsv(): Promise<void> {
  return downloadAdminCsv('/admin/reports/export.csv', `drivecars-reports-gmv-${todayIso()}.csv`);
}

// ─── Partners ────────────────────────────────────────────────────────

export interface AdminPartner {
  id: number;
  slug: string;
  name: string;
  secret_key_prefix: string;
  origin_allowlist: string[];
  mor_mode: 'drivecars' | 'partner_invoiced';
  booking_mode: 'variable' | 'fixed';
  commission_pct: number | null;
  commission_flat: number | null;
  platform_pct_override: number | null;
  pickup_buffer_override: number | null;
  billing_cycle_days: number;
  /** Where billing cycle 0 starts — `YYYY-MM-DD`, UTC. Statements in the
   *  partner console are cut on anchor + n × billing_cycle_days. */
  billing_cycle_anchor: string;
  theme_overrides: Record<string, unknown>;
  enabled_components: string[];
  // What the partner SELLS (rentals/transfers), gating /v1 — as opposed to
  // enabled_components above, which names the widgets their page may embed.
  // See packages/shared/src/partner-products.ts for why the two must not be
  // read as the same thing.
  enabled_products: string[];
  default_product: string | null;
  // Whose end-user identity is authoritative for this partner's shoppers
  // (migration 0219). 'drivecars' — we sign the shopper in; 'partner' — they
  // hand us their own user's token and we introspect it at the URL below.
  auth_provider: PartnerAuthProvider;
  auth_introspection_url: string | null;
  // A BOOLEAN, not the secret. The API never serialises the bearer it presents
  // to the partner's introspection endpoint — PartnerRow collapses the
  // ciphertext column to this one fact (apps/api/src/modules/partners/
  // service.ts). Ops needs to know a bearer is configured, not what it is.
  has_auth_introspection_secret: boolean;
  status: 'active' | 'suspended' | 'revoked';
  // DriveCars' own storefront row — the one partner that is us. It cannot be
  // put in `fixed` booking mode (the API refuses it with
  // `booking_mode_not_allowed`), because that would narrow every customer's
  // own grid to crews that opted in and turn a marketplace-wide contract into
  // a radio button. Read-only: nothing in the editor writes it.
  is_first_party: boolean;
  // Activated in every published city (migration 0239), as opposed to only the
  // cities in `app.partner_locations`. NOT written by the main editor Save —
  // it moves through PUT /admin/partners/:id/locations alone, so an unrelated
  // edit can never widen or narrow where a partner sells.
  all_locations: boolean;
  // Sells every crew (migration 0241), as opposed to only the crews in
  // `app.partner_vendors`. Moves through PUT /admin/partners/:id/vendors
  // alone, for the same reason all_locations does.
  all_vendors: boolean;
  // How DriveCars earns on this partner (0241), and at what rates. Read for
  // two purposes: the Commercial model fieldset edits them, and the two
  // supplier-commission models cannot sell a crew that has agreed no rate, so
  // the Included crews fieldset shows a warning badge for them.
  //
  // EVERY RATE IS A FRACTION (0241's CHECKs are 0..1), never a percentage. The
  // editor converts on the way in and out — see partner-commercial-model-state.ts.
  commercial_model: 'booking_fee' | 'supplier_commission' | 'markup_supplier_commission';
  /** Model 1: the partner's share OF THE FEE. NULL leaves the legacy
   *  commission_flat + commission_pct × price rule in force. */
  fee_share_pct: number | null;
  /** Model 3: our markup over the crew's base, and the partner's share of it. */
  markup_pct: number | null;
  markup_share_pct: number | null;
  /** Models 2/3: the partner's share of the commission the crew pays us. */
  supplier_commission_share_pct: number | null;
  // Merchant of record for the SDK CHANNEL only. `mor_mode` above is the
  // partner's general answer; /v1 is always partner-invoiced and our own
  // storefront/app are always DriveCars, both forced at token-mint time, so
  // the SDK embed is the only channel with a genuine choice.
  sdk_mor: 'partner' | 'drivecars';
  created_at: string;
  updated_at: string;
}

export interface AdminPartnersListResponse {
  partners: AdminPartner[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminPartnerResponse {
  partner: AdminPartner;
}

export interface AdminPartnerSecretResponse {
  partner: AdminPartner;
  oneTimeSecret: string;
}

export interface PartnerWriteInput {
  slug?: string;
  name: string;
  origin_allowlist: string[];
  mor_mode: 'drivecars' | 'partner_invoiced';
  booking_mode: 'variable' | 'fixed';
  commission_pct?: number | null;
  commission_flat?: number | null;
  platform_pct_override?: number | null;
  pickup_buffer_override?: number | null;
  // The commercial model (0241). Nullable on the four rates, and the null is
  // meaningful in the same way default_product's is: an admin clearing a rate
  // means "no share rule", which on fee_share_pct is what puts the legacy
  // commission back in force. The editor sends the whole draft, so clearing
  // works. Rates are FRACTIONS — the form converts from the percent ops types.
  commercial_model?: 'booking_fee' | 'supplier_commission' | 'markup_supplier_commission';
  fee_share_pct?: number | null;
  markup_pct?: number | null;
  markup_share_pct?: number | null;
  supplier_commission_share_pct?: number | null;
  sdk_mor?: 'partner' | 'drivecars';
  billing_cycle_days?: number;
  /** `YYYY-MM-DD`. Omit on create and the API uses today. */
  billing_cycle_anchor?: string;
  theme_overrides?: Record<string, unknown>;
  enabled_components?: string[];
  enabled_products?: string[];
  // Nullable, and the null is meaningful: the API distinguishes an omitted
  // default_product ("no opinion, keep what is there") from an explicit null
  // ("clear it"). The editor always sends the whole draft, so clearing works.
  default_product?: string | null;
  auth_provider?: PartnerAuthProvider;
  auth_introspection_url?: string | null;
  // WRITE-ONLY, and the optionality is load-bearing in a way none of the other
  // fields' is. Because the editor sends the WHOLE draft on every Save, a key
  // that is present with a null value clears the stored bearer — so for every
  // save that is not deliberately changing the secret this key must be ABSENT,
  // not null. See secretForPayload in routes/partner-auth-state.ts.
  auth_introspection_secret?: string | null;
}

export function fetchAdminPartnersList(
  params: PageParams = {},
  signal?: AbortSignal,
): Promise<AdminPartnersListResponse> {
  return api<AdminPartnersListResponse>({
    path: `/admin/partners${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminPartner(
  id: number,
  signal?: AbortSignal,
): Promise<AdminPartnerResponse> {
  return api<AdminPartnerResponse>({ path: `/admin/partners/${id}`, signal });
}

export function createPartner(
  data: PartnerWriteInput,
): Promise<AdminPartnerSecretResponse> {
  return api<AdminPartnerSecretResponse>({
    path: '/admin/partners',
    method: 'POST',
    body: data,
  });
}

export function updatePartner(
  id: number,
  data: Partial<PartnerWriteInput>,
): Promise<AdminPartnerResponse> {
  return api<AdminPartnerResponse>({
    path: `/admin/partners/${id}`,
    method: 'PATCH',
    body: data,
  });
}

export function rotatePartnerSecret(
  id: number,
): Promise<AdminPartnerSecretResponse> {
  return api<AdminPartnerSecretResponse>({
    path: `/admin/partners/${id}/rotate-secret`,
    method: 'POST',
  });
}

export function setPartnerStatus(
  id: number,
  action: 'suspend' | 'activate' | 'revoke',
): Promise<AdminPartnerResponse> {
  return api<AdminPartnerResponse>({
    path: `/admin/partners/${id}/${action}`,
    method: 'POST',
  });
}

export interface AdminPartnerStats {
  cycleDays: number;
  bookingsThisCycle: number;
  commissionThisCycle: number;
  bookingsAllTime: number;
}

export function fetchAdminPartnerStats(id: number, signal?: AbortSignal): Promise<AdminPartnerStats> {
  return api<AdminPartnerStats>({ path: `/admin/partners/${id}/stats`, signal });
}

// ─── Where a partner is activated (migration 0239) ───────────────────
//
// `all` is the flag (everywhere published); `cityIds` are the CITIES switched
// on for everyone else — an activated city implicitly activates the
// airports/stations beneath it, so those are never picked individually.
//
// Deliberately its own read + write rather than fields on the partner PATCH:
// activation must not be a side effect of saving a commission change, and a
// PUT that is a replace-set is the only shape in which "these cities and no
// others" is unambiguous.

export interface PartnerLocationsResponse {
  all: boolean;
  cityIds: number[];
}

export interface PartnerLocationsInput {
  all?: boolean;
  cityIds?: number[];
}

export function fetchPartnerLocations(
  id: number,
  signal?: AbortSignal,
): Promise<PartnerLocationsResponse> {
  return api<PartnerLocationsResponse>({ path: `/admin/partners/${id}/locations`, signal });
}

/** Replace-set. Answers with what is NOW stored, not with what was sent —
 *  the caller renders the response rather than its own optimistic guess.
 *  The API refuses `all: false` for DriveCars' own partners (409
 *  `first_party_everywhere`) and a non-city id (400 `not_a_city`). */
export function savePartnerLocations(
  id: number,
  body: PartnerLocationsInput,
): Promise<PartnerLocationsResponse> {
  return api<PartnerLocationsResponse>({
    path: `/admin/partners/${id}/locations`,
    method: 'PUT',
    body,
  });
}

// ─── Included crews (0241) ───────────────────────────────────────────
//
// WHICH CREWS a partner may sell, the supply-side twin of the cities block
// above and deliberately the same shape: `all` (every crew) or an explicit
// list, never both. Its own read + write for the same reason activation is —
// a crew list must not be re-sent as a side effect of saving a commission
// change, and a replace-set PUT is the only unambiguous way to say "these
// crews and no others".
//
// `warnings` has no counterpart on the cities side: a partner on a
// supplier-commission model is not served a crew that has agreed no rate,
// even though ops ticked it, and this is what puts the badge beside it.

export interface PartnerVendorsResponse {
  all: boolean;
  vendorIds: number[];
}

export interface PartnerVendorsInput {
  all?: boolean;
  vendorIds?: number[];
}

export interface PartnerVendorWarning {
  vendorId: number;
  name: string;
  reason: 'no_supplier_rate';
}

export function fetchPartnerVendors(
  id: number,
  signal?: AbortSignal,
): Promise<PartnerVendorsResponse> {
  return api<PartnerVendorsResponse>({ path: `/admin/partners/${id}/vendors`, signal });
}

/** Replace-set. Answers with what is NOW stored, not with what was sent. The
 *  API refuses `all: false` for DriveCars' own partners (409
 *  `first_party_everywhere`) and the house crew / a supplier / an unknown id
 *  (400 `vendor_not_allowed`). */
export function savePartnerVendors(
  id: number,
  body: PartnerVendorsInput,
): Promise<PartnerVendorsResponse> {
  return api<PartnerVendorsResponse>({
    path: `/admin/partners/${id}/vendors`,
    method: 'PUT',
    body,
  });
}

export function fetchPartnerVendorWarnings(
  id: number,
  signal?: AbortSignal,
): Promise<{ warnings: PartnerVendorWarning[] }> {
  return api<{ warnings: PartnerVendorWarning[] }>({
    path: `/admin/partners/${id}/vendors/warnings`,
    signal,
  });
}

export interface CreatePartnerFromApplicationInput {
  slug?: string;
  commissionPct?: number;
  commissionFlat?: number;
  morMode?: 'drivecars' | 'partner_invoiced';
  enabledComponents?: Array<'search' | 'cart' | 'checkout' | 'payment'>;
  billingCycleDays?: number;
}

// Onboard an approved applicant: creates the partner (returns the secret
// ONCE) and flips the application to 'approved'.
export function createPartnerFromApplication(
  appId: number,
  body: CreatePartnerFromApplicationInput,
): Promise<AdminPartnerSecretResponse> {
  return api<AdminPartnerSecretResponse>({
    path: `/admin/partner-applications/${appId}/create-partner`,
    method: 'POST',
    body,
  });
}

// ─── Partner API credentials ─────────────────────────────────────────
//
// OAuth client-credential pairs for the /v1 partner API — distinct from
// AdminPartner.secret_key_prefix (the bcrypt `dc_sk_` widget secret
// rotatePartnerSecret manages above). Backs apps/api/src/modules/admin/
// partners.ts's /admin/partners/:id/credentials routes.

export interface AdminPartnerCredential {
  id: number;
  partner_id: number;
  client_id: string;
  client_secret_prefix: string;
  label: string;
  scopes: string[];
  status: 'active' | 'retiring' | 'revoked';
  retire_after: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminPartnerCredentialsListResponse {
  credentials: AdminPartnerCredential[];
}

export interface AdminPartnerCredentialCreatedResponse {
  credential: AdminPartnerCredential;
  oneTimeSecret: string;
}

export interface AdminPartnerCredentialResponse {
  credential: AdminPartnerCredential;
}

export function fetchAdminPartnerCredentials(
  id: number,
  signal?: AbortSignal,
): Promise<AdminPartnerCredentialsListResponse> {
  return api<AdminPartnerCredentialsListResponse>({
    path: `/admin/partners/${id}/credentials`,
    signal,
  });
}

export function createAdminPartnerCredential(
  id: number,
  data: { label: string; scopes: string[] },
): Promise<AdminPartnerCredentialCreatedResponse> {
  return api<AdminPartnerCredentialCreatedResponse>({
    path: `/admin/partners/${id}/credentials`,
    method: 'POST',
    body: data,
  });
}

export function retireAdminPartnerCredential(
  id: number,
  credId: number,
  overlapHours?: number,
): Promise<AdminPartnerCredentialResponse> {
  return api<AdminPartnerCredentialResponse>({
    path: `/admin/partners/${id}/credentials/${credId}/retire`,
    method: 'POST',
    body: overlapHours != null ? { overlap_hours: overlapHours } : {},
  });
}

export function revokeAdminPartnerCredential(
  id: number,
  credId: number,
): Promise<AdminPartnerCredentialResponse> {
  return api<AdminPartnerCredentialResponse>({
    path: `/admin/partners/${id}/credentials/${credId}/revoke`,
    method: 'POST',
  });
}

// ─── Partner console team ────────────────────────────────────────────
//
// Who at the partner can sign in to partners.drivecars.ai. A partner's owners
// run their own team, so ops normally only needs this once per partner: to
// invite the FIRST owner. Backs /admin/partners/:id/members + /invite-owner.
// Same { members, invites } shape the console's own GET /members returns.

export type AdminPartnerRole = 'owner' | 'developer' | 'finance';

export interface AdminPartnerMember {
  partnerUserId: number;
  email: string;
  name: string | null;
  role: AdminPartnerRole;
  status: string;
  joinedAt: string | null;
}

export interface AdminPartnerInvite {
  id: number;
  email: string;
  role: AdminPartnerRole;
  status: string;
  expiresAt: string | null;
  invitedAt: string | null;
}

export interface AdminPartnerMembersResponse {
  members: AdminPartnerMember[];
  invites: AdminPartnerInvite[];
}

export interface AdminPartnerOwnerInvitedResponse {
  ok: true;
  invite: { email: string; role: 'owner'; expiresAt: string };
  /** False when the invite was recorded but the email could not be sent —
   *  inviting again reissues the link. */
  emailSent: boolean;
}

export function listPartnerMembers(
  id: number,
  signal?: AbortSignal,
): Promise<AdminPartnerMembersResponse> {
  return api<AdminPartnerMembersResponse>({
    path: `/admin/partners/${id}/members`,
    signal,
  });
}

export function invitePartnerOwner(
  id: number,
  email: string,
): Promise<AdminPartnerOwnerInvitedResponse> {
  return api<AdminPartnerOwnerInvitedResponse>({
    path: `/admin/partners/${id}/invite-owner`,
    method: 'POST',
    body: { email },
  });
}

// ─── Partner invoices (migration 0241) ───────────────────────────────
//
// The monthly two-way settlement with a partner: one document per calendar
// month PER CURRENCY, built from `bookingMoney`. `partnerOwes` is what the
// partner collected on DriveCars' behalf (its own merchant-of-record
// bookings); `drivecarsOwes` is the partner's shares on DriveCars-MoR ones;
// `net` is the difference, positive when the partner pays DriveCars.
//
// The collection hangs off the partner (where ops is already standing); the
// item has its own prefix so a row can be acted on by id alone.

export type AdminPartnerInvoiceStatus = 'draft' | 'issued' | 'paid' | 'void';

export type AdminPartnerInvoiceLineKind =
  | 'fee_share'
  | 'markup_share'
  | 'supplier_commission_share'
  | 'drivecars_fee'
  | 'drivecars_markup'
  | 'adjustment';

export interface AdminPartnerInvoiceLine {
  id: number;
  bookingId: number | null;
  reference: string | null;
  model: string | null;
  kind: AdminPartnerInvoiceLineKind;
  amount: number;
  currency: string;
  /** Which way the line points. Derived server-side from `kind`, so the
   *  screen never re-encodes the mapping. */
  side: 'partner_owes' | 'drivecars_owes';
}

export interface AdminPartnerInvoice {
  id: number;
  partnerId: number;
  partnerName: string | null;
  partnerSlug: string | null;
  cycleIndex: number;
  /** YYYY-MM-DD. `periodEnd` is the first day of the NEXT month. */
  periodStart: string;
  periodEnd: string;
  currency: string;
  partnerOwes: number;
  drivecarsOwes: number;
  net: number;
  bookingCount: number;
  status: AdminPartnerInvoiceStatus;
  issuedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface AdminPartnerInvoicesResponse {
  invoices: AdminPartnerInvoice[];
  /** The month ops would most likely generate, so the picker can default to
   *  it without an operator doing calendar arithmetic. */
  currentCycleIndex: number;
}

export interface AdminPartnerInvoiceDetailResponse {
  invoice: AdminPartnerInvoice & { lines: AdminPartnerInvoiceLine[] };
}

export interface AdminPartnerInvoiceGeneratedResponse {
  cycleIndex: number;
  invoices: AdminPartnerInvoice[];
}

export function fetchPartnerInvoices(
  partnerId: number,
  signal?: AbortSignal,
): Promise<AdminPartnerInvoicesResponse> {
  return api<AdminPartnerInvoicesResponse>({
    path: `/admin/partners/${partnerId}/invoices`,
    signal,
  });
}

export function generatePartnerInvoice(
  partnerId: number,
  cycleIndex: number,
): Promise<AdminPartnerInvoiceGeneratedResponse> {
  return api<AdminPartnerInvoiceGeneratedResponse>({
    path: `/admin/partners/${partnerId}/invoices/generate`,
    method: 'POST',
    body: { cycleIndex },
  });
}

export function fetchPartnerInvoice(
  invoiceId: number,
  signal?: AbortSignal,
): Promise<AdminPartnerInvoiceDetailResponse> {
  return api<AdminPartnerInvoiceDetailResponse>({
    path: `/admin/partner-invoices/${invoiceId}`,
    signal,
  });
}

/** draft → issued | issued → paid | draft,issued → void. The API refuses any
 *  other move with a 409; the caller turns that into a sentence. */
export function transitionPartnerInvoice(
  invoiceId: number,
  action: 'issue' | 'mark-paid' | 'void',
): Promise<{ ok: true; invoice: AdminPartnerInvoice }> {
  return api<{ ok: true; invoice: AdminPartnerInvoice }>({
    path: `/admin/partner-invoices/${invoiceId}/${action}`,
    method: 'POST',
  });
}

// ─── Backoffice staff (admin users) ──────────────────────────────────
//
// Backs the /users route — list + create + edit + suspend/activate + reset
// password. Mirrors the AdminCustomerRow shape so the table renderer can
// reuse the same Avatar + StatusPill + DataTable plumbing.

export interface AdminUsersListParams {
  status?: 'active' | 'pending' | 'suspended' | 'all';
  q?: string;
  page?: number;
  limit?: number;
}

export type AdminStaffRole =
  | 'super_admin' | 'operations' | 'finance' | 'marketing' | 'support' | 'viewer';

export interface AdminUserRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  phone: string | null;
  role: 'admin' | 'customer' | 'vendor';
  /** Backoffice scoped role (0093). NULL for admins not yet assigned a
   *  role — they pass requireAdmin but fail every requireSection check. */
  staffRole: AdminStaffRole | null;
  status: 'active' | 'pending' | 'suspended';
  verified: boolean | null;
  twoFactorEnabled: boolean | null;
  createdAt: string;
  updatedAt: string;
  avatarUrl: string | null;
}

export interface AdminUsersListResponse {
  rows: AdminUserRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAdminUsers(
  params: AdminUsersListParams = {},
  signal?: AbortSignal,
): Promise<AdminUsersListResponse> {
  return api<AdminUsersListResponse>({
    path: `/admin/users${buildQuery(params)}`,
    signal,
  });
}

// The create endpoint hard-codes role='admin' server-side (the staff
// management URL must not mint customers/vendors). The field is omitted
// from the payload shape for the same reason.
export interface CreateAdminUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  password: string;
  status?: 'active' | 'pending' | 'suspended';
}

export function createAdminUser(body: CreateAdminUserInput): Promise<{ id: number }> {
  return api<{ id: number }>({ path: '/admin/users', method: 'POST', body });
}

export interface UpdateAdminUserInput {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  role?: 'admin' | 'customer' | 'vendor';
  status?: 'active' | 'pending' | 'suspended';
  /** Set NULL to revoke all scoped section access while keeping the
   *  user as role='admin' (effectively a sandboxed staff account). */
  staffRole?: AdminStaffRole | null;
}

export function updateAdminUser(id: number, body: UpdateAdminUserInput): Promise<{ ok: true }> {
  return api({ path: `/admin/users/${id}`, method: 'PATCH', body });
}

export const suspendAdminUser = (id: number) =>
  mutate<{ ok: true }>(`/admin/users/${id}/suspend`);
export const activateAdminUser = (id: number) =>
  mutate<{ ok: true }>(`/admin/users/${id}/activate`);
export const resetAdminUserPassword = (id: number, newPassword: string) =>
  mutate<{ ok: true }>(`/admin/users/${id}/reset-password`, { newPassword });

// ─── Vehicle Catalog (admin CRUD — /admin/vehicle-catalog/*) ─────────────────
//
// Makes → Models → Entries hierarchy with localized copy, specs, feature links,
// and image management (presign → browser PUT → finalize).

export type CatalogLocale = 'en' | 'fr' | 'ar' | 'es';

export interface CatalogMakeRow {
  id: number;
  slug: string;
  name: string;
  updatedAt: string;
}

export interface CatalogMakesResponse {
  rows: CatalogMakeRow[];
  total: number;
}

export interface CatalogModelRow {
  id: number;
  slug: string;
  name: string;
  updatedAt: string;
}

export interface CatalogModelsResponse {
  rows: CatalogModelRow[];
  total: number;
}

export interface CatalogEntryListRow {
  id: number;
  slug: string;
  defaultTitle: string;
  year: number;
  trim: string;
  isActive: boolean;
  updatedAt: string;
  modelSlug: string;
  modelName: string;
  makeSlug: string;
  makeName: string;
}

export interface CatalogEntriesResponse {
  rows: CatalogEntryListRow[];
  total: number;
}

export interface CatalogLocaleCopy {
  title?: string | null;
  description?: string | null;
  seoMetaTitle?: string | null;
  seoMetaDescription?: string | null;
}

export interface CatalogSpecs {
  passenger?: number | null;
  doors?: number | null;
  gearShift?: string | null;
  baggage?: number | null;
  fuelType?: string | null;
  bodyType?: string | null;
}

export interface CatalogEntryImage {
  id: number;
  order: number;
  mediaId: number;
  sourceKey: string;
  isHero: boolean;
  url: string;
}

export interface CatalogEntryRecord {
  id: number;
  modelId: number;
  /** Parent make id (via the model) — used by the ?entry= deep-link. */
  makeId: number | null;
  /** Null for listing-seeded entries — free-form titles carry no model year. */
  year: number | null;
  trim: string;
  slug: string;
  defaultTitle: string;
  isActive: boolean;
  updatedAt: string;
  specs: CatalogSpecs;
  locales: Partial<Record<CatalogLocale, CatalogLocaleCopy>>;
  featureIds: number[];
  images: CatalogEntryImage[];
}

export interface CatalogEntrySavePayload {
  modelId: number;
  year: number | null;
  trim?: string;
  specs?: CatalogSpecs;
  featureIds?: number[];
  isActive?: boolean;
  locales?: Partial<Record<CatalogLocale, CatalogLocaleCopy>>;
}

export interface CatalogPresignResponse {
  uploadUrl: string;
  key: string;
  contentType: string;
}

export interface CatalogFinalizeResponse {
  linkId: number;
  key: string;
}

export function fetchCatalogMakes(signal?: AbortSignal): Promise<CatalogMakesResponse> {
  return api<CatalogMakesResponse>({ path: '/admin/vehicle-catalog/makes', signal });
}

export function createCatalogMake(body: { slug: string; name: string }): Promise<{ id: number }> {
  return api<{ id: number }>({
    path: '/admin/vehicle-catalog/makes',
    method: 'POST',
    body,
  });
}

export function patchCatalogMake(
  id: number,
  body: { slug?: string; name?: string },
): Promise<{ ok: true; id: number }> {
  return api<{ ok: true; id: number }>({
    path: `/admin/vehicle-catalog/makes/${id}`,
    method: 'PATCH',
    body,
  });
}

export function deleteCatalogMake(id: number): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/makes/${id}`,
    method: 'DELETE',
  });
}

export function fetchCatalogModels(
  makeId: number,
  signal?: AbortSignal,
): Promise<CatalogModelsResponse> {
  return api<CatalogModelsResponse>({
    path: `/admin/vehicle-catalog/makes/${makeId}/models`,
    signal,
  });
}

export function createCatalogModel(body: {
  makeId: number;
  slug: string;
  name: string;
}): Promise<{ id: number }> {
  return api<{ id: number }>({
    path: '/admin/vehicle-catalog/models',
    method: 'POST',
    body,
  });
}

export function deleteCatalogModel(id: number): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/models/${id}`,
    method: 'DELETE',
  });
}

export function fetchCatalogEntries(
  params: { makeId?: number; modelId?: number; year?: number } = {},
  signal?: AbortSignal,
): Promise<CatalogEntriesResponse> {
  return api<CatalogEntriesResponse>({
    path: `/admin/vehicle-catalog/entries${buildQuery(params)}`,
    signal,
  });
}

export function fetchCatalogEntry(
  id: number,
  signal?: AbortSignal,
): Promise<CatalogEntryRecord> {
  return api<CatalogEntryRecord>({
    path: `/admin/vehicle-catalog/entries/${id}`,
    signal,
  });
}

export function saveCatalogEntry(
  id: number | null,
  body: CatalogEntrySavePayload,
): Promise<{ id: number }> {
  return id != null
    ? api<{ id: number }>({
        path: `/admin/vehicle-catalog/entries/${id}`,
        method: 'PATCH',
        body,
      })
    : api<{ id: number }>({
        path: '/admin/vehicle-catalog/entries',
        method: 'POST',
        body,
      });
}

export function deleteCatalogEntry(id: number): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/entries/${id}`,
    method: 'DELETE',
  });
}

export function presignCatalogImage(
  entryId: number,
  body: { originalFilename: string; contentType: string; sizeBytes: number },
): Promise<CatalogPresignResponse> {
  return api<CatalogPresignResponse>({
    path: `/admin/vehicle-catalog/entries/${entryId}/images/presign`,
    method: 'POST',
    body,
  });
}

export function finalizeCatalogImage(
  entryId: number,
  body: { key: string; originalFilename: string; contentType: string; sizeBytes: number; order?: number; hero?: boolean },
): Promise<CatalogFinalizeResponse> {
  return api<CatalogFinalizeResponse>({
    path: `/admin/vehicle-catalog/entries/${entryId}/images/finalize`,
    method: 'POST',
    body,
  });
}

export function reorderCatalogImages(
  entryId: number,
  order: number[],
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/entries/${entryId}/images/reorder`,
    method: 'PATCH',
    body: { order },
  });
}

export function setCatalogImageHero(
  entryId: number,
  imageId: number,
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/entries/${entryId}/images/${imageId}/hero`,
    method: 'POST',
  });
}

export function deleteCatalogImage(
  entryId: number,
  imageId: number,
): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/entries/${entryId}/images/${imageId}`,
    method: 'DELETE',
  });
}

// ─── Vehicle Catalog — AI Retrieve (metadata + image) ────────────────────────
//
// POST .../entries/:id/retrieve/metadata → fills specs + all-locale copy.
// POST .../entries/:id/retrieve/image    → fetches + attaches an image server-side.
//
// Both return 503 `{ error: 'ai_not_configured' }` when OPENAI_API_KEY is unset.
// We unwrap that into a thrown Error whose message is the body `error` string so
// the UI can show a friendly "AI is not configured yet." instead of a raw status.

async function callRetrieve<T>(entryId: number, action: 'metadata' | 'image'): Promise<T> {
  try {
    return await api<T>({
      path: `/admin/vehicle-catalog/entries/${entryId}/retrieve/${action}`,
      method: 'POST',
    });
  } catch (err) {
    // Surface 503 ai_not_configured as a plain Error whose message is the
    // body's `error` field so callers can branch on it without importing ApiError.
    const apiErr = err as { status?: number; body?: { error?: string } };
    if (apiErr.status === 503 && apiErr.body?.error) {
      throw new Error(apiErr.body.error);
    }
    throw err;
  }
}

export function retrieveCatalogMetadata(
  entryId: number,
): Promise<{ specs: CatalogSpecs; locales: Record<CatalogLocale, CatalogLocaleCopy> }> {
  return callRetrieve<{ specs: CatalogSpecs; locales: Record<CatalogLocale, CatalogLocaleCopy> }>(
    entryId,
    'metadata',
  );
}

// Returns 202 { queued: true } — generation takes 40-90s (longer than the API
// gateway allows a response to wait), so the server hands it off to a
// background task. Poll the entry record until the new hero image appears.
export function retrieveCatalogImage(
  entryId: number,
): Promise<{ queued: boolean }> {
  return callRetrieve<{ queued: boolean }>(entryId, 'image');
}

export interface BulkFillResult {
  processed: number;
  updated: number;
  failed: Array<{ id: number; error: string }>;
  nextCursor: number | null;
}

// POST /admin/vehicle-catalog/entries/bulk-fill — regenerate + PERSIST metadata +
// features for a bounded batch (server caps to ~8/request to stay under the gateway
// timeout, since each entry is one ~5-15s gpt-4o call). Pass the returned
// `nextCursor` back until it's null to walk the whole catalog. Throws the plain
// `ai_not_configured` message on a 503, matching callRetrieve.
export async function bulkFillCatalogMetadata(
  cursor = 0,
): Promise<BulkFillResult> {
  try {
    return await api<BulkFillResult>({
      path: '/admin/vehicle-catalog/entries/bulk-fill',
      method: 'POST',
      body: { cursor },
    });
  } catch (err) {
    const apiErr = err as { status?: number; body?: { error?: string } };
    if (apiErr.status === 503 && apiErr.body?.error) {
      throw new Error(apiErr.body.error);
    }
    throw err;
  }
}

// POST .../entries/retrieve/metadata (no id) → AI specs + all-locale copy for an
// entry that hasn't been created yet. Powers the "Generate with AI" button on
// the new-entry form; the operator reviews the prefilled fields, then saves.
export async function generateCatalogMetadataForNew(input: {
  modelId: number;
  year?: number;
  trim?: string;
}): Promise<{ specs: CatalogSpecs; locales: Record<CatalogLocale, CatalogLocaleCopy> }> {
  try {
    return await api<{ specs: CatalogSpecs; locales: Record<CatalogLocale, CatalogLocaleCopy> }>({
      path: `/admin/vehicle-catalog/entries/retrieve/metadata`,
      method: 'POST',
      body: input,
    });
  } catch (err) {
    const apiErr = err as { status?: number; body?: { error?: string } };
    if (apiErr.status === 503 && apiErr.body?.error) throw new Error(apiErr.body.error);
    throw err;
  }
}

// ─── Vendor products + airport transfers ────────────────────────────────────

export type VendorProduct = 'car-rentals' | 'airport-transfers' | 'ride-hailing';

export interface TransferClassOption {
  id: number;
  slug: string;
  name: string;
}

export interface TransferAirportOption {
  id: number;
  slug: string;
  name: string;
  countryCode: string | null;
  /** Currency this airport's prices should be quoted in (MAD/AED). Defaulting
   *  the field matters: 250 is a plausible number in both, so a wrong currency
   *  is invisible to validation and off by ~2.5x. */
  currency: string;
}

export interface VendorTransferPrice {
  locationId: number;
  transferClassId: number;
  price: number;
  currency: string;
  active: boolean;
}

export interface VendorProductsPayload {
  products: VendorProduct[];
  airports: Array<{ id: number; slug: string; name: string }>;
  prices: VendorTransferPrice[];
}

export function fetchVendorProducts(
  id: number,
  signal?: AbortSignal,
): Promise<VendorProductsPayload> {
  return api({ path: `/admin/vendors/${id}/products`, signal });
}

export function putVendorProducts(
  id: number,
  products: VendorProduct[],
): Promise<{ ok: true; products: VendorProduct[] }> {
  return api({ path: `/admin/vendors/${id}/products`, method: 'PUT', body: { products } });
}

export function putVendorTransferAirports(
  id: number,
  locationIds: number[],
): Promise<{ ok: true; locationIds: number[] }> {
  return api({
    path: `/admin/vendors/${id}/transfer-airports`,
    method: 'PUT',
    body: { locationIds },
  });
}

export function putVendorTransferPrices(
  id: number,
  prices: VendorTransferPrice[],
): Promise<{ ok: true; count: number }> {
  return api({ path: `/admin/vendors/${id}/transfer-prices`, method: 'PUT', body: { prices } });
}

export function fetchTransferClasses(
  signal?: AbortSignal,
): Promise<{ rows: TransferClassOption[] }> {
  return api({ path: '/admin/transfer-classes', signal });
}

export function fetchTransferAirports(
  signal?: AbortSignal,
): Promise<{ rows: TransferAirportOption[] }> {
  return api({ path: '/admin/transfer-airports', signal });
}

// ─── Vendor availability exclusions ──────────────────────────────────────
//
// Ops-side mirror of the crew app's own screen (Task 5's
// /admin/vendors/:id/availability-exclusions) — same shapes, an explicit
// vendor id in the path instead of a scope.

export interface VendorAvailabilityExclusion {
  id: number;
  vendorId: number;
  product: 'rentals' | 'transfers' | null;
  categoryId: number | null;
  categoryName: string | null;
  dateFrom: string;
  dateTo: string;
  note: string | null;
  // 'vendor' = a vendors-audience login: the COMPANY itself rather than a
  // person on the crew (which is what 'crew' means, an app.crew_users row).
  createdBy: { kind: 'crew' | 'admin' | 'vendor'; id: number; email: string | null };
  createdAt: string;
}

export interface VendorAvailabilityPayload {
  exclusions: VendorAvailabilityExclusion[];
  pickLists: {
    vehicleTypes: Array<{ id: number; name: string; slug: string }>;
    transferClasses: Array<{ id: number; name: string; slug: string }>;
    sellsTransfers: boolean;
  };
  today: string;
}

export function fetchVendorAvailability(
  vendorId: number,
  signal?: AbortSignal,
): Promise<VendorAvailabilityPayload> {
  return api({ path: `/admin/vendors/${vendorId}/availability-exclusions`, signal });
}

export function createVendorExclusion(
  vendorId: number,
  body: { product: 'rentals' | 'transfers' | null; categoryId: number | null; dateFrom: string; dateTo: string; note: string | null },
): Promise<{ exclusion: VendorAvailabilityExclusion }> {
  return api({ path: `/admin/vendors/${vendorId}/availability-exclusions`, method: 'POST', body });
}

export function deleteVendorExclusion(
  vendorId: number,
  exclusionId: number,
): Promise<{ ok: true }> {
  return api({ path: `/admin/vendors/${vendorId}/availability-exclusions/${exclusionId}`, method: 'DELETE' });
}

// ─── Drivers ─────────────────────────────────────────────────────────
//
// Deliberately separate from vendors/crew: a crew is a company, a driver is a
// licensed individual with expiring documents and per-city eligibility.

export type DriverFilter = 'all' | 'online' | 'expiring' | 'pending_docs' | 'suspended';

export interface AdminDriver {
  id: number;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  email: string | null;
  status: string;
  rating: number | null;
  tripsCount: number;
  homeCity: string | null;
  online: boolean;
  lastSeenAt: string | null;
  /** Comma-joined crew names; null when they are on no crew. */
  crews: string | null;
  docsExpiring: number;
  docsExpired: number;
  docsPending: number;
  eligibleServices: string[];
  openJobs: number;
}

export interface AdminDriverDocument {
  id: number;
  kind: string;
  status: string;
  fileRef: string | null;
  expiresAt: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  plate: string | null;
}

/** One city, with a per-service allowed flag — the design's eligibility grid. */
export interface AdminDriverEligibility {
  locationId: number;
  city: string | null;
  services: Record<string, boolean>;
  reasons: string[];
}

export interface AdminDriverVehicle {
  id: number;
  plate: string;
  makeModel: string | null;
  year: number | null;
  fuel: string | null;
  seats: number | null;
  isActive: boolean;
}

export interface AdminDriverSession {
  id: number;
  wentOnlineAt: string;
  wentOfflineAt: string | null;
  lastSeenAt: string | null;
  city: string | null;
}

export interface AdminDriverDetail {
  driver: AdminDriver;
  documents: AdminDriverDocument[];
  eligibility: AdminDriverEligibility[];
  vehicles: AdminDriverVehicle[];
  sessions: AdminDriverSession[];
}

export interface AdminDriversListResponse {
  rows: AdminDriver[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export function fetchAdminDrivers(
  params: { filter?: DriverFilter; q?: string; page?: number; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AdminDriversListResponse> {
  return api<AdminDriversListResponse>({
    path: `/admin/drivers${buildQuery(params)}`,
    signal,
  });
}

export function fetchAdminDriver(
  id: number,
  signal?: AbortSignal,
): Promise<AdminDriverDetail> {
  return api<AdminDriverDetail>({ path: `/admin/drivers/${id}`, signal });
}

export function setDriverStatus(
  id: number,
  status: 'active' | 'suspended' | 'blocked',
  reason?: string,
): Promise<{ ok: true }> {
  return api({ path: `/admin/drivers/${id}`, method: 'PATCH', body: { status, reason } });
}

export function reviewDriverDocument(
  driverId: number,
  docId: number,
  status: 'approved' | 'rejected',
  reviewNote?: string,
): Promise<{ ok: true }> {
  return api({
    path: `/admin/drivers/${driverId}/documents/${docId}`,
    method: 'PATCH',
    body: { status, reviewNote },
  });
}

// ─── Driver wallet, payout accounts and payouts ─────────────────────
//
// A driver may take walk-in fares from day one; nobody is paid until ops has
// approved where the money goes. That approval is the vetting. There is no
// withdraw: DriveCars pays out on a schedule and each transfer is recorded.

export interface AdminPayoutAccount {
  id: number;
  driverId: number;
  driverName: string | null;
  driverPhone: string | null;
  holderName: string;
  bankName: string;
  /** In full. Comparing it with the driver's paperwork IS the review. */
  accountNumber: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
}

export interface AdminDriverWalletTotals {
  currency: string;
  earned: number;
  paidOut: number;
  balance: number;
}

export interface AdminDriverLedgerEntry {
  id: string;
  kind: 'charge' | 'payout';
  at: string;
  label: string | null;
  gross: number;
  commission: number;
  net: number;
  currency: string;
}

export interface AdminDriverWallet {
  totals: AdminDriverWalletTotals[];
  entries: AdminDriverLedgerEntry[];
  /** Newest first. accounts[0] is the driver's current account. */
  accounts: AdminPayoutAccount[];
}

export function fetchPayoutAccountQueue(
  signal?: AbortSignal,
): Promise<{ rows: AdminPayoutAccount[] }> {
  return api({ path: '/admin/drivers/payout-accounts/list?status=pending', signal });
}

export function fetchAdminDriverWallet(
  driverId: number,
  signal?: AbortSignal,
): Promise<AdminDriverWallet> {
  return api({ path: `/admin/drivers/${driverId}/wallet`, signal });
}

export function reviewPayoutAccount(
  accountId: number,
  decision: 'approved' | 'rejected',
  note?: string,
): Promise<{ account: AdminPayoutAccount }> {
  return api({
    path: `/admin/drivers/payout-accounts/${accountId}/review`,
    method: 'POST',
    body: { decision, note },
  });
}

export function recordDriverPayout(
  driverId: number,
  input: { amount: number; currency: string; reference: string; note?: string },
): Promise<{ ok: true; id: number }> {
  return api({ path: `/admin/drivers/${driverId}/payouts`, method: 'POST', body: input });
}

// ─── Walk-in rides (ops view) ───────────────────────────────────────

export interface AdminRide {
  id: number;
  status: string;
  phase: 'waiting' | 'riding' | 'finished' | 'cancelled';
  amount: number;
  heldAmount: number | null;
  currency: string;
  dropoffLabel: string | null;
  createdAt: string;
  heldAt: string | null;
  settledAt: string | null;
  driver: { id: number; name: string | null; phone: string | null };
  passenger: { firstName: string | null; phone: string | null; email: string | null };
  vehicle: { plate: string; model: string | null } | null;
  /** Only while the ride is RUNNING. The server withholds it afterwards. */
  position: { latitude: number; longitude: number; lastSeenSecondsAgo: number } | null;
  /** The passenger's own tracking page. */
  trackUrl: string | null;
}

export function fetchAdminRides(
  scope: 'running' | 'all',
  signal?: AbortSignal,
): Promise<{ rows: AdminRide[] }> {
  return api({ path: `/admin/drivers/rides/list?scope=${scope}`, signal });
}

// ─── Airport requests ────────────────────────────────────────────────
//
// A crew asking for an airport we have not onboarded. Ops resolves by
// pointing the request at a real app.locations row — crews never write
// shared marketplace geography themselves.

export interface AirportRequestRow {
  id: number;
  vendorId: number;
  crewName: string | null;
  airportName: string;
  city: string | null;
  countryCode: string;
  iataCode: string | null;
  notes: string | null;
  status: string;
  fulfilledLocationId: number | null;
  fulfilledName: string | null;
  rejectReason: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface AirportRequestsResponse {
  requests: AirportRequestRow[];
  total: number;
  page: number;
  limit: number;
}

export function fetchAirportRequests(
  params: PageParams & {
    status?: 'pending' | 'approved' | 'rejected' | 'all';
  } = {},
  signal?: AbortSignal,
): Promise<AirportRequestsResponse> {
  return api({
    path: `/admin/airport-requests${buildQuery({ status: 'pending', ...params })}`,
    signal,
  });
}

export function resolveAirportRequest(
  id: number,
  body:
    | { status: 'approved'; fulfilledLocationId: number }
    | { status: 'rejected'; rejectReason: string },
): Promise<{ ok: true }> {
  return api({ path: `/admin/airport-requests/${id}`, method: 'PATCH', body });
}

// ─── Transfer rate rules ─────────────────────────────────────────────────────
//
// Seasonal / night / weekend / short-notice uplifts (migration 0194). A rule
// matches when every condition it states holds; when several match, the
// STRONGEST SINGLE rule applies — they never compound.

export interface TransferRateRule {
  id: number;
  /** null = an ops rule. Non-null = that crew's own. */
  vendorId: number | null;
  vendorName: string | null;
  /** One airport, or null with a countryCode for a whole market. */
  locationId: number | null;
  airportName: string | null;
  countryCode: string | null;
  /** null = every transfer class. */
  transferClassId: number | null;
  className: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  /** Local hour band, half-open. hourTo < hourFrom wraps midnight. */
  hourFrom: number | null;
  hourTo: number | null;
  /** ISO 1=Mon..7=Sun. */
  daysOfWeek: number[] | null;
  leadDaysMin: number | null;
  leadDaysMax: number | null;
  multiplier: number | null;
  price: number | null;
  currency: string | null;
  label: string;
  active: boolean;
  priority: number;
}

export interface TransferRuleAirport {
  id: number;
  slug: string;
  name: string;
  countryCode: string | null;
  /** The ceiling on published tariff uplift here. 1.00 = none permitted. */
  upliftCap: number;
  /** False when no crew prices this airport yet — every fare reads "not
   *  available", which is a supply gap rather than a fault. */
  hasPrices?: boolean;
}

export type TransferRuleInput = Omit<
  TransferRateRule,
  'id' | 'vendorName' | 'airportName' | 'className'
>;

export function fetchTransferRules(
  signal?: AbortSignal,
): Promise<{ rules: TransferRateRule[]; airports: TransferRuleAirport[] }> {
  return api({ path: '/admin/transfer-rules', signal });
}

export function createTransferRule(body: Partial<TransferRuleInput>): Promise<{ id: number }> {
  return api({ path: '/admin/transfer-rules', method: 'POST', body });
}

export function updateTransferRule(
  id: number,
  body: Partial<TransferRuleInput>,
): Promise<{ ok: true }> {
  return api({ path: `/admin/transfer-rules/${id}`, method: 'PATCH', body });
}

/**
 * Turn a rule on or off WITHOUT touching its conditions.
 *
 * Not a PATCH: PATCH is a full replace, so re-enabling through it would wipe
 * the rule's hour band, season and day list. Ops may do this to ANY rule,
 * including a crew's — the crew keeps ownership and can still edit it.
 */
export function deleteTransferRule(id: number): Promise<{ ok: true }> {
  return api({ path: `/admin/transfer-rules/${id}/permanent`, method: 'DELETE' });
}

export function setTransferRuleActive(id: number, active: boolean): Promise<{ ok: true }> {
  return api({ path: `/admin/transfer-rules/${id}/active`, method: 'POST', body: { active } });
}

export interface TransferFarePreview {
  classId: number;
  name: string;
  currency: string;
  /** What the customer pays on the previewed date and hour. */
  price: number | null;
  /** What they would pay with no rule applied. */
  basePrice: number | null;
  appliedRule: { ruleId: number; label: string; multiplier: number } | null;
  vendorCount: number;
}

/** Run the REAL resolver for a date and hour. Same code path as the customer's
 *  quote — a preview computed separately would eventually disagree with the
 *  price actually charged, which is worse than having no preview. */
export function previewTransferFares(body: {
  airportSlug: string;
  date: string;
  hour: number;
}): Promise<{ classes: TransferFarePreview[] }> {
  return api({ path: '/admin/transfer-rules/preview', method: 'POST', body });
}

// ─── Transfer price change requests ──────────────────────────────────────────
//
// A crew asking to change a price inside its year-long commitment window
// (0193/0196). Approval applies the price but does NOT restart the lock — the
// commitment is to a period, not to a price.

export interface TransferPriceRequest {
  id: number;
  vendorId: number;
  vendorName: string | null;
  airportName: string | null;
  className: string | null;
  currentPrice: number | null;
  newPrice: number;
  currency: string;
  reason: string;
  status: 'pending' | 'approved' | 'declined' | 'withdrawn';
  decisionNote: string | null;
  requestedAt: string;
  /** How much of the commitment is left — what ops is being asked to release
   *  them from. */
  lockedUntil: string | null;
}

export function fetchTransferPriceRequests(
  status: 'pending' | 'approved' | 'declined' | 'all' = 'pending',
  signal?: AbortSignal,
): Promise<{ requests: TransferPriceRequest[] }> {
  return api({ path: `/admin/transfer-rules/price-requests?status=${status}`, signal });
}

/** A DECLINE requires a note — refused server-side too. A refusal with no
 *  reason teaches the crew nothing and they will simply ask again. */
export function decideTransferPriceRequest(
  id: number,
  body: { approve: boolean; note?: string },
): Promise<{ ok: true; approved: boolean }> {
  return api({
    path: `/admin/transfer-rules/price-requests/${id}/decide`,
    method: 'POST',
    body,
  });
}

// ─── Supplier-commission statements ──────────────────────────────────
//
// The monthly bill a crew owes DriveCars on Model 2/3 sales. Per-currency:
// a crew billing in MAD and EUR has TWO statements a month and they are never
// added together. See apps/api/src/modules/billing/vendor-statements.ts.

export type VendorStatementStatus = 'open' | 'issued' | 'paid';

export interface VendorStatementLine {
  ledgerId: number;
  bookingId: number;
  reference: string;
  accruedAt: string;
  baseAmount: number;
  /** The crew's agreed rate as a FRACTION (0.10 = 10%), snapshotted at
   *  accrual so a renegotiation does not restate an old bill. */
  ratePct: number;
  amount: number;
  partnerShare: number;
  currency: string;
}

export interface VendorStatementRow {
  id: number;
  vendorId: number;
  vendorName: string | null;
  cycleIndex: number;
  periodStart: string;
  periodEnd: string;
  currency: string;
  amount: number;
  bookingCount: number;
  status: VendorStatementStatus;
  issuedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface VendorStatementDetail extends VendorStatementRow {
  lines: VendorStatementLine[];
}

export interface VendorStatementsListParams {
  vendorId?: number;
  status?: VendorStatementStatus;
  cycleIndex?: number;
  page?: number;
  limit?: number;
}

export interface VendorStatementsListResponse {
  rows: VendorStatementRow[];
  total: number;
  page: number;
  limit: number;
  /** The month ops would generate next, so the screen offers it without the
   *  operator doing calendar arithmetic. */
  currentCycleIndex: number;
}

export function fetchVendorStatements(
  params: VendorStatementsListParams = {},
  signal?: AbortSignal,
): Promise<VendorStatementsListResponse> {
  return api<VendorStatementsListResponse>({
    path: `/admin/vendor-statements${buildQuery(params)}`,
    signal,
  });
}

export function fetchVendorStatement(
  id: number,
  signal?: AbortSignal,
): Promise<VendorStatementDetail> {
  return api<VendorStatementDetail>({ path: `/admin/vendor-statements/${id}`, signal });
}

export function generateVendorStatements(
  cycleIndex: number,
): Promise<{ cycleIndex: number; statements: unknown[] }> {
  return api({
    path: '/admin/vendor-statements/generate',
    method: 'POST',
    body: { cycleIndex },
  });
}

export function issueVendorStatement(
  id: number,
): Promise<{ ok: true; statement: VendorStatementDetail }> {
  return api({ path: `/admin/vendor-statements/${id}/issue`, method: 'POST' });
}

export function markVendorStatementPaid(
  id: number,
): Promise<{ ok: true; statement: VendorStatementDetail }> {
  return api({ path: `/admin/vendor-statements/${id}/mark-paid`, method: 'POST' });
}
